import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import {
  getExpirationDaysForDocType,
  isHardDurationDocType,
  isExplicitExpirationDocType,
  isDualDateDocType,
  deriveFromExpirationDate,
  deriveDualDateFields,
  computeExpirationDate,
} from "../../../../constants/documents/docTypeExpirationDurations";
import DocExpirationCell from "./DocExpirationCell";
import { AutoDurationPill } from "./DocDurationCell";
// icons
import {
  Add,
  ApartmentOutlined,
  ArrowUpwardOutlined,
  BoltOutlined,
  CancelOutlined,
  CheckCircleOutline,
  ChevronRightOutlined,
  CloseFullscreenOutlined,
  CloseRounded,
  ContentPasteGoOutlined,
  EditOutlined,
  FileUploadOutlined,
  HourglassEmptyOutlined,
  HouseOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInFullOutlined,
  PersonOutline,
  ViewModuleOutlined,
  VisibilityOffOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Container,
  Fade,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  amber,
  blue,
  green,
  grey,
  orange,
  red,
  yellow,
} from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { has, isEmpty, size } from "lodash";
import { v4 as uuidv4 } from "uuid";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import FileUploadField from "../../../../components/inputs/FileUploadField";
import LoanFilePreviewModal from "../../../../components/modals/files/LoanFilePreviewModal";
import SearchInput from "../../../../components/inputs/SearchInput";
import SelectFileAssignmentInput from "../../../../components/inputs/SelectFileAssignmentInput";
import DocTypePickerField from "../../../../components/inputs/docTypePicker/DocTypePickerField";
import InputWrapper from "../../../../components/inputs/shared/InputWrapper";
import Flx from "../../../../components/layout/Flx";
import FilterTabSwitcher from "../../../../components/navigation/FilterTabSwitcher";
import Txt from "../../../../components/typography/Txt";
import { useFileDocGroupAndTypesHook } from "../../../../hooks/useFileDocGroupAndTypesHook";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { sidebarSetValues } from "../../../../redux/actions/sidebarActions";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  DETAIL_SIDEBAR_CONTENT_TYPES,
  detailSidebarOpen,
} from "../../../../redux/reducers/detailSidebarReducer";
import LoanDocumentManagerTable, {
  EditDocumentDetailsDialog,
  DEFAULT_LAYOUT,
  isBorrowerPortalUpload,
  isDocHidden,
  LAYOUT_STORAGE_KEY,
  LoanDocumentLayoutToggle,
  LoanDocumentViewToggle,
  DOC_VIEW_OPTIONS,
  DOC_VIEW_TITLES,
} from "./LoanDocumentManagerTable";
import { DOCUMENTS_VIEW_PARAM } from "../../components/drilldownSubTabs";
import DocGroupCoverageView from "./DocGroupCoverageView";
import MissingDocumentsTable from "./MissingDocumentsTable";
import { UploadQueueProvider, useUploadQueue } from "./UploadQueueContext";
import { useChecklistRouteHook } from "../../../../hooks/useChecklistRouteHook";
import { getStatusStyle } from "../../../../components/agGrid/cellRenderer/LoanFileStatusCell";
import {
  selectOptionsDocumentStatus,
  documentStatusLabel,
} from "../../../../constants/selectOptions/selectOptionsDocumentStatus";
import DocValidityPanel from "./DocValidityPanel";
import { buildValidityModel, idsForFilter } from "./docValidityModel";
import {
  useDrilldownBaseUrl,
  useIsAcquisitions,
} from "../../../../pipelines/PipelineContext";
import { DocumentValidityBar } from "./DocumentValidityWidget";
import DocumentInsightCards, {
  INSIGHTS_EXPANDED_KEY,
} from "./DocumentInsightCards";
import { teal } from "../../../../theme/colors";
import useLocalStorageState from "use-local-storage-state";
import { formatPluralByArraySize } from "../../../../_src_shared/utils/functions/formatPluralByArraySize";
import { setView } from "azure-maps-control";
import LoadingFallback from "../../../../components/LoadingFallback";
import LoanDrilldownTabContentWrapper from "../LoanDrilldownTabContentWrapper";
import { nsKey } from "../../../../utils/storage/partnerStorage";
import LoanExtractsViewer from "./extracts/LoanExtractsViewer";
import { canSeeLoanExtracts } from "./extracts/loanExtractsAccess";
import LoanOcrolusPanel from "./ocrolus/LoanOcrolusPanel";
import useOcrolusBook from "./ocrolus/useOcrolusBook";
import ExtractTrifoldModal from "./extracts/ExtractTrifoldModal";
import { useLoanExtractsIndex } from "./extracts/useLoanExtractsIndex";
import { useAuth } from "../../../../auth/AuthProvider";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

// Exported so the reusable LoanDocumentUploadModal drives LoanDocumentUploaderProcess
// with the same view values.
export const UPLOAD_TYPES = {
  QUICK_UPLOAD: "quick_upload",
  GROUP: "group",
};

const LoanDrilldownDocumentsTab = ({ value }) => {
  const loan = useSelector((state) => state.loanDrilldown);
  const loanDocuments = loan?.loanDocuments;
  const { user } = useAuth();
  const showExtracts = canSeeLoanExtracts(user?.email);
  const [uploadType, setUploadType] = useState(UPLOAD_TYPES.QUICK_UPLOAD);
  // Document extracts viewer (3-pane OCR / structured-JSON / images) — opened
  // from the header, fetches its index on open.
  const [extractsOpen, setExtractsOpen] = useState(false);

  // Extracted-document artifacts (OCR markdown / structured JSON / images),
  // produced by the Box → LOS sync. Fetched once here so the Parsed tab badge
  // and the Extracted Documents table share a single request; gated to the
  // extracts allowlist so non-allowlisted users make no request.
  const { documents: extractedDocuments } = useLoanExtractsIndex(loan?._id, {
    enabled: showExtracts,
  });

  // Ocrolus book status — fetched here so the Ocrolus tab badge and the panel
  // share a single request, the same way the extracts index is handled above.
  const {
    book: ocrolusBook,
    state: ocrolusState,
    filesSent: ocrolusFilesSent,
    loading: ocrolusLoading,
    error: ocrolusError,
    reload: reloadOcrolus,
  } = useOcrolusBook(loan?._id);

  // Index the extracts by both the parent document id and the source file's
  // storage name, so an uploaded-table row can tell whether it's been parsed
  // (either key may match) and open its extract. `parsedDocKeys` drives the
  // "Parsed" chip; `extractByKey` resolves the chip click to its extract entry.
  const { parsedDocKeys, extractByKey } = useMemo(() => {
    const keys = new Set();
    const byKey = new Map();
    for (const d of extractedDocuments || []) {
      const p = d?.parent || d?.parent_provenance || {};
      if (d?.parent_document_id) {
        keys.add(d.parent_document_id);
        byKey.set(d.parent_document_id, d);
      }
      if (p?.file_storage_name) {
        keys.add(p.file_storage_name);
        byKey.set(p.file_storage_name, d);
      }
    }
    return { parsedDocKeys: keys, extractByKey: byKey };
  }, [extractedDocuments]);

  // Trifold (tri-panel) opened from a "Parsed" chip in the uploaded table.
  const [trifoldExtractId, setTrifoldExtractId] = useState(null);
  // The null guard is load-bearing: "nothing selected" is `null`, and an extract
  // whose `parent_document_id` is missing is also null — so an unguarded `find`
  // matches one of those the moment the index loads and opens the trifold by
  // itself, every time the tab is opened and again on every close.
  const trifoldExtract = useMemo(
    () =>
      (trifoldExtractId
        ? (extractedDocuments || []).find(
            (d) => d.parent_document_id === trifoldExtractId,
          )
        : null) || null,
    [extractedDocuments, trifoldExtractId],
  );
  const handleOpenParsedDoc = useCallback(
    (doc) => {
      const entry =
        extractByKey.get(doc?.file_storage_name) || extractByKey.get(doc?._id);
      if (entry) setTrifoldExtractId(entry.parent_document_id);
    },
    [extractByKey],
  );
  // Review-status filter: clicking a segment/legend chip filters the documents
  // table to that documentStatus (null = no filter).
  const [reviewFilter, setReviewFilter] = useState(null);
  // Validity filter: clicking a validity item filters the table to that group
  // (valid|expiringSoon|expired). Combines with the review-status filter.
  const [validityFilter, setValidityFilter] = useState(null);
  // Hidden-file scope on the Uploaded tab, driven by the "Shown vs hidden"
  // card: "visible" (default) | "total" (include hidden) | "hidden" (only
  // hidden). It widens the Uploaded list in place instead of sending the user
  // to the Hidden tab. Unlike the two filters above this always has a value —
  // it's a three-way selector, not a toggle.
  const [visibilityScope, setVisibilityScope] = useState("visible");
  // ── View + grouping (two independent axes) ──────────────────────────
  // `viewMode` = which subset of docs (the tab switcher): "visible" | "hidden"
  // | "missing" | "external" | "parsed". `grouping` = the two toolbar toggles
  // (category / type). These replaced the single combined `layoutMode`.
  // The view is the URL: `<loan base>/documents/<path>` (base is /loan/:id or
  // /acquisition/:id — useDrilldownBaseUrl picks it per pipeline). Every view
  // runs on both pipelines; Missing included, since
  // /checklists/check_missing_documents resolves the deal out of EITHER one.
  const navigate = useNavigate();
  const baseUrl = useDrilldownBaseUrl(loan?._id);
  const subPath = (useParams()["*"] || "").split("/")[0];
  const routeView = DOC_VIEW_OPTIONS.find((o) => o.path === subPath);
  const effectiveViewMode = routeView?.key || "visible";
  // Uploaded is the bare `…/documents`; every other view is `…/documents/<path>`.
  const viewUrl = useCallback(
    (path) => (path ? `${baseUrl}/documents/${path}` : `${baseUrl}/documents`),
    [baseUrl],
  );
  const setViewMode = useCallback(
    (key) => {
      const view = DOC_VIEW_OPTIONS.find((o) => o.key === key);
      if (view && baseUrl) navigate(viewUrl(view.path));
    },
    [navigate, baseUrl, viewUrl],
  );
  // An unknown sub-path (incl. the old `/documents/uploaded`) or an old
  // `?view=<key>` link → redirect (replace) to the matching route, so the
  // address bar always names the view on screen.
  const [searchParams] = useSearchParams();
  const legacyView = searchParams.get(DOCUMENTS_VIEW_PARAM);
  useEffect(() => {
    if (!baseUrl) return;
    if (routeView && !legacyView) return;
    const target =
      DOC_VIEW_OPTIONS.find((o) => o.key === legacyView)?.path ??
      routeView?.path ??
      "";
    navigate(viewUrl(target), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, subPath, legacyView]);
  // The hidden-file scope belongs to the Uploaded tab. Leaving it drops back
  // to the default so returning later doesn't land on a widened list the
  // operator has no memory of choosing.
  useEffect(() => {
    if (effectiveViewMode !== "visible") setVisibilityScope("visible");
  }, [effectiveViewMode]);
  // Same key the insight panel toggles — read here so the table toolbar can
  // tighten its top padding while the panel above it is collapsed.
  const [insightsExpanded] = useLocalStorageState(INSIGHTS_EXPANDED_KEY, {
    defaultValue: false,
  });
  // The insight panel only sits above the table on the document-list views.
  const showInsights =
    effectiveViewMode !== "missing" &&
    effectiveViewMode !== "ocrolus" &&
    effectiveViewMode !== "coverage" &&
    effectiveViewMode !== "validity" &&
    !isEmpty(loanDocuments);
  const [grouping, setGrouping] = useLocalStorageState(
    nsKey("loanDocumentManagerGrouping"),
    { defaultValue: { status: false, category: false, type: false } },
  );
  const groupByStatus = !!grouping?.status;
  const groupByCategory = !!grouping?.category;
  const groupByType = !!grouping?.type;
  // Map the three independent axes to a layout key. Status always leads (top
  // level); Category then Type nest beneath. Neither → flat Table List.
  const groupingKey = useMemo(() => {
    const s = groupByStatus;
    const c = groupByCategory;
    const t = groupByType;
    if (s && c && t) return "statusDocGroupAndType";
    if (s && c) return "statusDocGroup";
    if (s && t) return "statusDocType";
    if (s) return "status";
    if (c && t) return "docGroupAndType";
    if (c) return "docGroup";
    if (t) return "docType";
    return "none";
  }, [groupByStatus, groupByCategory, groupByType]);
  const toggleGroupStatus = useCallback(
    () => setGrouping((g) => ({ ...(g || {}), status: !g?.status })),
    [setGrouping],
  );
  const toggleGroupCategory = useCallback(
    () => setGrouping((g) => ({ ...(g || {}), category: !g?.category })),
    [setGrouping],
  );
  const toggleGroupType = useCallback(
    () => setGrouping((g) => ({ ...(g || {}), type: !g?.type })),
    [setGrouping],
  );

  // Missing documents (checklist-derived requirements) drive the "Missing"
  // view + its badge count. Fetched once per loan.
  const { getMissingDocuments } = useChecklistRouteHook();
  const [missingDocs, setMissingDocs] = useState(null);
  // The check RESOLVES an acquisition deal but then hands it to requirement
  // functions written against the preclose loan model
  // (`all_loan_requirements` calls `get_file_upload_type_and_status_counts`,
  // which only exists on UnderwritingBaseModel) — so postclose 500s today.
  // Hold the failure rather than showing an empty list, which would read as
  // "nothing missing".
  const [missingError, setMissingError] = useState(null);
  const fetchMissing = useCallback(() => {
    if (!loan?._id) return;
    getMissingDocuments({
      dealId: loan._id,
      onSuccessFn: (data) => {
        setMissingError(null);
        setMissingDocs(data?.missing_documents || []);
      },
      onFailFn: (err) => {
        setMissingError(err || true);
        setMissingDocs([]);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loan?._id]);
  useEffect(() => {
    fetchMissing();
  }, [fetchMissing]);
  const missingCount = missingDocs?.length || 0;
  // Fullscreen toggle — drops the card's padding and lifts its
  // max-width so the table claims the full viewport. Persisted so the
  // setting survives page reloads (e.g. an underwriter who works in
  // fullscreen most of the time doesn't have to re-toggle).
  const [fullscreen, setFullscreen] = useLocalStorageState(
    nsKey("loanDocumentManagerFullscreen"),
    { defaultValue: false },
  );

  // Status counts feed the per-status tiles. Counts run over ALL docs
  // (the status filter UI was removed in favor of this passive widget).
  const allDocs = useMemo(() => loanDocuments || [], [loanDocuments]);
  const totalCount = allDocs.length;
  const hiddenCount = useMemo(
    () => allDocs.filter(isDocHidden).length,
    [allDocs],
  );
  // Hidden files are excluded from every count below — by-status, validity
  // and the active total. Hidden docs only ever surface via their own count.
  const visibleDocs = useMemo(
    () => allDocs.filter((d) => !isDocHidden(d)),
    [allDocs],
  );
  // Count files uploaded directly by the borrower (not hidden) so the
  // "Borrower Uploads" tab in LoanDocumentLayoutToggle gets a badge.
  const borrowerPortalUploadCount = useMemo(
    () => visibleDocs.filter((d) => isBorrowerPortalUpload(d)).length,
    [visibleDocs],
  );
  const activeCount = totalCount - hiddenCount;
  // Parsed view = the Uploaded table narrowed to (non-hidden) docs that have
  // an extract, the same way Hidden is the table narrowed to hidden docs.
  const isDocParsed = useCallback(
    (d) => parsedDocKeys.has(d?.file_storage_name) || parsedDocKeys.has(d?._id),
    [parsedDocKeys],
  );
  const parsedDocs = useMemo(
    () => visibleDocs.filter(isDocParsed),
    [visibleDocs, isDocParsed],
  );
  // The Status / Validity charts break down the SELECTED tab document set:
  // Uploaded -> visible (non-hidden), Hidden -> hidden, External -> borrower
  // uploads. (Missing / Parsed hide the charts entirely.)
  const tabDocs = useMemo(() => {
    if (effectiveViewMode === "hidden") return allDocs.filter(isDocHidden);
    if (effectiveViewMode === "external")
      return visibleDocs.filter((d) => isBorrowerPortalUpload(d));
    if (effectiveViewMode === "parsed") return parsedDocs;
    // Uploaded tab — follow the Shown vs hidden scope so the Review progress
    // and Expirations cards describe the same set the table is showing.
    if (visibilityScope === "total") return allDocs;
    if (visibilityScope === "hidden") return allDocs.filter(isDocHidden);
    return visibleDocs;
  }, [effectiveViewMode, allDocs, visibleDocs, visibilityScope, parsedDocs]);
  const statusCounts = useMemo(() => {
    const counts = Object.fromEntries(
      selectOptionsDocumentStatus.map((s) => [s, 0]),
    );
    for (const d of tabDocs) {
      const s = d?.documentStatus;
      if (s && counts[s] !== undefined) counts[s] += 1;
    }
    return counts;
  }, [tabDocs]);

  // Association breakdown for the Associations insight card. The model is
  // INVERSE — each borrower / property / entity carries an
  // `associations.loanDocuments` list — so build a doc-id set per collection
  // then classify each doc into ONE bucket by priority (borrower › property ›
  // entity › unassigned) so the donut sums to the doc total.
  const associationCounts = useMemo(() => {
    const idsFrom = (collection) => {
      const set = new Set();
      (collection || []).forEach((item) =>
        (item?.associations?.loanDocuments || []).forEach((id) => set.add(id)),
      );
      return set;
    };
    const entityList = Array.isArray(loan?.entities)
      ? loan.entities
      : loan?.borrowerEntity
        ? [loan.borrowerEntity]
        : [];
    const borrowerIds = idsFrom(loan?.borrowers);
    const propertyIds = idsFrom(loan?.subjectProperties);
    const entityIds = idsFrom(entityList);
    const counts = { borrower: 0, property: 0, entity: 0, unassociated: 0 };
    for (const d of tabDocs) {
      const id = d?._id;
      if (borrowerIds.has(id)) counts.borrower += 1;
      else if (propertyIds.has(id)) counts.property += 1;
      else if (entityIds.has(id)) counts.entity += 1;
      else counts.unassociated += 1;
    }
    return counts;
  }, [
    tabDocs,
    loan?.borrowers,
    loan?.subjectProperties,
    loan?.entities,
    loan?.borrowerEntity,
  ]);

  // Document validity — every dated doc judged on the loan's reference days
  // (today, and the upcoming closing; or the frozen close for a closed loan).
  // The rules live in the shared helpers — see "Document validity &
  // expiration" in CLAUDE.md; docValidityModel only shapes them. Hidden docs
  // follow the tab's own set so the counts match what the table shows.
  const isAcquisition = useIsAcquisitions();
  const validityModel = useMemo(
    () => buildValidityModel({ loan, documents: tabDocs, isAcquisition }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tabDocs,
      isAcquisition,
      loan?.loanStatus,
      loan?.loanClosed,
      loan?.originationDate,
      loan?.fundedDate,
      loan?.estimatedClosingDate,
      loan?.expectedClosingDate,
      loan?.closingScheduledDate,
    ],
  );
  // The Document Validity sub-tab always looks at the loan's non-hidden docs,
  // whatever scope the Uploaded tab was left on.
  const validityTabModel = useMemo(
    () => buildValidityModel({ loan, documents: visibleDocs, isAcquisition }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      visibleDocs,
      isAcquisition,
      loan?.loanStatus,
      loan?.loanClosed,
      loan?.originationDate,
      loan?.fundedDate,
      loan?.estimatedClosingDate,
      loan?.expectedClosingDate,
      loan?.closingScheduledDate,
    ],
  );
  // Badge on the Document Validity sub-tab: what needs replacing — expired on
  // the status-setting day, plus what lapses before closing.
  const validityAttentionCount = useMemo(
    () =>
      validityTabModel.rows.filter(
        (r) => r.at[validityTabModel.statusKey] === "expired" || r.lapse,
      ).length,
    [validityTabModel],
  );
  // "Open in documents table" from the sub-tab: land on Uploaded with that
  // filter applied.
  const openValidityInTable = (filter) => {
    setValidityFilter(filter || null);
    setViewMode("visible");
  };
  // Table rows after the review-status + validity filters (both may apply).
  const documentsForTable = useMemo(() => {
    let docs = loanDocuments || [];
    if (reviewFilter)
      docs = docs.filter((d) => d?.documentStatus === reviewFilter);
    // "<refKey>:<status>" | "anchor:lapse" | "missingStart" — see
    // docValidityModel.parseFilter.
    const ids = idsForFilter(validityModel, validityFilter);
    if (ids) docs = docs.filter((d) => ids.has(d._id));
    if (effectiveViewMode === "parsed") docs = docs.filter(isDocParsed);
    return docs;
  }, [
    loanDocuments,
    reviewFilter,
    validityFilter,
    validityModel,
    effectiveViewMode,
    isDocParsed,
  ]);

  return (
    <UploadQueueProvider
      onAfterUpload={() => setUploadType(UPLOAD_TYPES.QUICK_UPLOAD)}
    >
      {/* Sub-tab view switcher at the very top, above the upload section. */}
      <LoanDocumentViewToggle
        value={effectiveViewMode}
        onChange={setViewMode}
        uploadedCount={activeCount}
        hiddenCount={hiddenCount}
        missingCount={missingCount}
        externalCount={borrowerPortalUploadCount}
        // Counts the rows the Parsed table shows (loan docs with an extract).
        extractedCount={showExtracts ? parsedDocs.length : 0}
        // No book yet → no badge, rather than a misleading "(0)".
        ocrolusCount={ocrolusState === "none" ? null : ocrolusFilesSent}
        validityCount={validityAttentionCount || null}
      />
      {/* The upload section (Quick Upload / By Category) belongs to the file
          views. Ocrolus is about the loan's BOOK, Coverage carries its own
          per-type upload pills, and Missing has its own header + Upload action —
          so none of them show it; the body starts right under the strip.
          Hidden lists files already set aside and Parsed lists files already
          extracted, so uploading there makes no sense either. */}
      {effectiveViewMode !== "ocrolus" &&
        effectiveViewMode !== "hidden" &&
        effectiveViewMode !== "parsed" &&
        effectiveViewMode !== "coverage" &&
        effectiveViewMode !== "missing" &&
        effectiveViewMode !== "validity" && (
          <LoanDrilldownTabContentWrapper
            subTitle={"Upload files freely or organize them by category"}
            title="Documents"
            // Match the page-title scale used by the LoanTabShell tabs
            // (Applicants / Properties) — see tabTokens.type.pageTitle.
            titleSx={{
              fontSize: "21px",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.25,
              color: "#0f172a",
            }}
            maxWidth={false}
            bodyMargin={0}
            endContent={
              <Flx ac g={1}>
                <FilterTabSwitcher
                  fontSize={11}
                  // Active label + icon in the primary action teal (teal-600).
                  activeColor={teal[600]}
                  value={uploadType}
                  sx={{
                    // background: "#f2f1ef",
                    // background: "none",
                    // border: "1px solid #e5e3df",
                    // borderRadius: "4px",
                    // -----
                    background: "#0000000a",
                    border: "1px solid #e0e5ec",
                    ".MuiButtonBase-root": {
                      py: 1.2,
                      "&.selected": {
                        background: "#ffffff",
                        border: "1px solid #e5e3df",
                      },
                    },
                    // // -----
                    // background: "#ffffff",
                    // border: "1px solid #3341554d",
                    // ".MuiButtonBase-root": {
                    //   py: 1.2,
                    //   "&.selected": {
                    //     background: "#334155",
                    //     border: "1px solid #e5e3df",
                    //   },
                    // },
                  }}
                  onChange={setUploadType}
                  tabs={[
                    {
                      label: "Quick Upload",
                      value: UPLOAD_TYPES.QUICK_UPLOAD,
                      icon: <ArrowUpwardOutlined />,
                    },
                    {
                      label: "Upload By Category",
                      value: UPLOAD_TYPES.GROUP,
                      icon: <ViewModuleOutlined />,
                    },
                  ]}
                />
              </Flx>
            }
          >
            <LoanDocumentUploaderProcess
              view={uploadType}
              setView={setUploadType}
            />
            {/* Insight cards sit directly under the upload, within the section's
            padding — document views only (Missing / Parsed / Ocrolus render
            their own bodies). */}
          </LoanDrilldownTabContentWrapper>
        )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {showInsights && (
          <Box
          //
          // sx={{ mt: 2 }}
          >
            <DocumentInsightCards
              title={
                effectiveViewMode === "hidden"
                  ? "Hidden Documents"
                  : effectiveViewMode === "parsed"
                    ? "Parsed Documents"
                    : "Uploaded Documents"
              }
              statusCounts={statusCounts}
              // Review progress is measured against the set actually on
              // screen (the tab's view + the hidden-file scope); the
              // Shown vs hidden card always shows the loan's true split.
              activeCount={tabDocs.length}
              visibleCount={activeCount}
              hiddenCount={hiddenCount}
              validityModel={validityModel}
              onOpenValidityTab={() => setViewMode("validity")}
              reviewFilter={reviewFilter}
              onToggleStatus={(k) =>
                setReviewFilter((cur) => (cur === k ? null : k))
              }
              validityFilter={validityFilter}
              onToggleValidity={(k) =>
                setValidityFilter((cur) => (cur === k ? null : k))
              }
              associationCounts={associationCounts}
              // The scope selector only makes sense on the Uploaded tab —
              // the Hidden and Borrower-uploads tabs ARE a hidden-ness
              // filter already, so there the card stays a read-only summary.
              visibilityScope={
                effectiveViewMode === "visible" ? visibilityScope : undefined
              }
              onSelectVisibilityScope={
                effectiveViewMode === "visible" ? setVisibilityScope : undefined
              }
            />
          </Box>
        )}
        <Box
          sx={{
            // No paper of its own on ANY sub-view: every view sits on the
            // drilldown's page gray (the same gray behind every other tab), so
            // a short view never leaves a white band — or a second gray — below
            // its content. Cards and the grid bring their own white.
            background: "transparent",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          {/* The Ocrolus tab is about the loan's BOOK, not its documents — a
              book can be created (or assigned) before anything is uploaded, so
              it opts out of the no-documents empty state. Coverage
              renders the full catalog regardless — an empty loan is all gaps,
              which is exactly what it shows. */}
          {isEmpty(loanDocuments) &&
          effectiveViewMode !== "ocrolus" &&
          effectiveViewMode !== "coverage" ? (
            <Flx sx={{ p: 3 }}>
              <Txt sx={{ fontStyle: "italic" }}>
                No documents added yet for this loan.
              </Txt>
            </Flx>
          ) : (
            // Coverage paints its own canvas, so it has to fill the column —
            // otherwise a short (filtered) grid leaves the white host showing
            // beneath it.
            <Flx column fw flexGrow={effectiveViewMode === "coverage" ? 1 : 0}>
              <Suspense fallback={<LoadingFallback />}>
                {effectiveViewMode === "validity" ? (
                  // Every dated doc on one timeline, judged on today and the
                  // upcoming closing (or the frozen close). Its own page frame
                  // (LoanTabShell, as on Contacts): the header carries the
                  // title, the counts line and the day toggle.
                  <Box sx={{ flexGrow: 1 }}>
                    <DocValidityPanel
                      shell
                      model={validityTabModel}
                      onOpenInTable={openValidityInTable}
                    />
                  </Box>
                ) : effectiveViewMode === "coverage" ? (
                  // Doc-group → doc-type catalog with the loan's files overlaid.
                  // Its upload pills stage into the same FixedUploadQueue.
                  <Box sx={{ flexGrow: 1 }}>
                    <DocGroupCoverageView />
                  </Box>
                ) : effectiveViewMode === "missing" ? (
                  missingError ? (
                    <Flx column ac jc g={0.75} sx={{ py: 7, px: 3 }}>
                      <Txt sx={{ fontSize: 14, fontWeight: 600 }}>
                        The requirement check couldn't run for this loan
                      </Txt>
                      <Txt
                        sx={{
                          fontSize: 12.5,
                          color: "#64748b",
                          textAlign: "center",
                        }}
                      >
                        Nothing is known about what's outstanding — this is not
                        a clean file. Every other Documents view still works.
                      </Txt>
                      <Button
                        size="small"
                        variant="text"
                        onClick={fetchMissing}
                        sx={{ mt: 0.5 }}
                      >
                        Try again
                      </Button>
                    </Flx>
                  ) : (
                    <MissingDocumentsTable
                      rowData={missingDocs || []}
                      loan={loan}
                      onUploaded={fetchMissing}
                    />
                  )
                ) : effectiveViewMode === "ocrolus" ? (
                  // Ocrolus book lifecycle + status. The SEND action isn't here
                  // — it needs selected rows, so it stays on the Uploaded tab's
                  // bulk bar; this panel routes the user back there.
                  <LoanOcrolusPanel
                    dealId={loan?._id}
                    book={ocrolusBook}
                    state={ocrolusState}
                    loading={ocrolusLoading}
                    error={ocrolusError}
                    reload={reloadOcrolus}
                    onGoToUploaded={() => setViewMode("visible")}
                  />
                ) : (
                  <LoanDocumentManagerTable
                    rowData={documentsForTable}
                    // Filter axis (Visible / Hidden / External) and grouping
                    // axis (the two toolbar toggles) are now independent.
                    viewFilter={effectiveViewMode}
                    toolbarPadding={
                      showInsights && !insightsExpanded
                        ? { pt: 1, pb: 1, px: 2 }
                        : { pt: 2, pb: 1, px: 2 }
                    }
                    visibilityScope={visibilityScope}
                    forcedLayout={groupingKey}
                    showGroupToggles
                    groupByStatus={groupByStatus}
                    groupByCategory={groupByCategory}
                    groupByType={groupByType}
                    onToggleGroupStatus={toggleGroupStatus}
                    onToggleGroupCategory={toggleGroupCategory}
                    onToggleGroupType={toggleGroupType}
                    hideFilenameDocTypeSubline
                    enableUserSettings
                    // "Parsed" chip on rows that have an extract; clicking it
                    // opens the trifold viewer for that document.
                    parsedDocKeys={parsedDocKeys}
                    onOpenParsedDoc={handleOpenParsedDoc}
                    // Parsed view: the filename opens the extract viewer
                    // (trifold) instead of the plain file preview.
                    onOpenFile={
                      effectiveViewMode === "parsed"
                        ? handleOpenParsedDoc
                        : undefined
                    }
                  />
                )}
              </Suspense>
            </Flx>
          )}
        </Box>
      </Box>
      <LoanExtractsViewer
        open={extractsOpen}
        onClose={() => setExtractsOpen(false)}
        dealId={loan?._id}
      />
      {/* Full-page trifold (Source · Markdown · Structured JSON) opened from a
          "Parsed" chip on an uploaded-document row. */}
      <ExtractTrifoldModal
        open={!!trifoldExtract}
        dealId={loan?._id}
        documents={extractedDocuments}
        selectedId={trifoldExtractId}
        onSelectId={setTrifoldExtractId}
        onClose={() => setTrifoldExtractId(null)}
      />
      {/* Single fixed, bottom-docked review panel, fed by every dropzone
          (Quick Upload / By Category) AND the Missing-documents rows. */}
      <FixedUploadQueue />
    </UploadQueueProvider>
  );
};

// Compact Overview + By Status counts shown on the right of the
// "Uploaded Loan Documents" title. Flat row of tiles (no card framing)
// so it sits cleanly opposite the title without competing visually with
// the table card below.
const DocumentOverviewStatusCountsRow = ({
  totalCount,
  activeCount,
  hiddenCount,
  statusCounts,
}) => {
  return (
    <Flx ac g={0} wrap sx={{ flexShrink: 0 }}>
      {/* <CountTile label="Total" count={totalCount} />
      <CountTile label="Active" count={activeCount} />
      <CountTile
        label="Hidden"
        count={hiddenCount}
        accentColor={hiddenCount > 0 ? "#ca8a04" : undefined}
      />
      <Box
        sx={{
          width: "1px",
          alignSelf: "stretch",
          bgcolor: "#dde1e7",
          mx: 1,
          my: 0.5,
        }}
      /> */}
      {/* Visible / Hidden totals on the left. Hidden files are excluded from
          the status counts on the right; Visible is their sum. */}
      <CountTile
        label="Visible"
        count={activeCount}
        tooltip="Files not marked hidden. The sum of the status counts on the right."
      />
      <CountTile
        label="Hidden"
        count={hiddenCount}
        dotColor="#ca8a04"
        accentColor={hiddenCount > 0 ? "#ca8a04" : undefined}
        tooltip="Files marked hidden. Excluded from the status and validity counts."
      />
      <Box
        sx={{
          width: "1px",
          alignSelf: "stretch",
          bgcolor: "#dde1e7",
          mx: 1,
          my: 0.5,
        }}
      />
      {selectOptionsDocumentStatus.map((status) => {
        const style = getStatusStyle(status);
        return (
          <CountTile
            key={status}
            label={documentStatusLabel(status)}
            count={statusCounts?.[status] ?? 0}
            dotColor={style?.dot}
          />
        );
      })}
    </Flx>
  );
};

// Segmented "REVIEW STATUS" bar shown opposite the title. Replaces the flat
// count tiles: a single proportional bar of the five document statuses with a
// "N of M accepted" readout, hover tooltips, and a legend row underneath.
const REVIEW_STATUS_SEGMENTS = [
  {
    key: "Accepted",
    label: "Underwriter Accepted",
    color: "#1D9E75",
    tint: "#E1F5EE",
    text: "#085041",
    border: "#9FE1CB",
  },
  {
    key: "Pending Review",
    label: "Pending",
    color: "#EF9F27",
    tint: "#FAEEDA",
    text: "#633806",
    border: "#FAC775",
  },
  {
    key: "Processor Reviewed",
    label: "Processor Reviewed",
    color: "#7C6BD6",
    tint: "#EEEBFA",
    text: "#372A78",
    border: "#CBC1EF",
  },
  {
    key: "New Upload",
    label: "New",
    color: "#378ADD",
    tint: "#E6F1FB",
    text: "#0C447C",
    border: "#B5D4F4",
  },
  {
    key: "Incomplete",
    label: "Incomplete",
    color: "#888780",
    tint: "#F1EFE8",
    text: "#444441",
    border: "#D3D1C7",
  },
  {
    key: "Rejected",
    label: "Rejected",
    color: "#E24B4A",
    tint: "#FCEBEB",
    text: "#791F1F",
    border: "#F7C1C1",
  },
];

// Interactive review-status bar: clicking a segment OR legend chip toggles a
// status filter on the documents table (owned by the tab via activeStatus /
// onToggleStatus so it can filter the grid rowData).
const DocumentReviewStatusBar = ({
  statusCounts,
  activeCount,
  hiddenCount,
  activeStatus,
  onToggleStatus,
}) => {
  const segments = REVIEW_STATUS_SEGMENTS.map((s) => ({
    ...s,
    count: statusCounts?.[s.key] ?? 0,
  }));
  const total = segments.reduce((sum, s) => sum + s.count, 0);
  const accepted = statusCounts?.["Accepted"] ?? 0;
  const acceptedPct = total ? Math.round((accepted / total) * 100) : 0;
  const present = segments.filter((s) => s.count > 0);
  const toggle = (key) => onToggleStatus?.(key);

  return (
    <Flx ac g={1.5} sx={{ flexShrink: 0 }}>
      <Flx column g={0.5} sx={{ flexShrink: 0, width: 420, maxWidth: "100%" }}>
        <Flx ac jb fw>
          <Txt
            sx={{
              fontSize: 10,
              fontWeight: 400,
              letterSpacing: "0.02em",
              color: "#52514e",
            }}
          >
            Status
          </Txt>
          <Txt sx={{ fontSize: 10, fontWeight: 400, color: "#475569" }}>
            <Box component="span" sx={{ color: "#1D9E75", fontWeight: 700 }}>
              {accepted}
            </Box>{" "}
            of {total} underwriter accepted
            <Box component="span" sx={{ color: "#94a3b8" }}>
              {" "}
              · {acceptedPct}%
            </Box>
          </Txt>
        </Flx>

        {/* Proportional segmented bar */}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: 8,
            borderRadius: "999px",
            overflow: "hidden",
            bgcolor: "#eef2f6",
          }}
        >
          {total === 0
            ? null
            : present.map((s) => {
                const dim = activeStatus && activeStatus !== s.key;
                return (
                  <Tooltip
                    key={s.key}
                    title={`${s.label}: ${s.count} — click to ${
                      activeStatus === s.key ? "clear" : "filter"
                    }`}
                    placement="top"
                    arrow
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => toggle(s.key)}
                      sx={{
                        flexGrow: s.count,
                        flexBasis: 0,
                        minWidth: 6,
                        border: "none",
                        p: 0,
                        bgcolor: s.color,
                        cursor: "pointer",
                        opacity: dim ? 0.35 : 1,
                        transition: "opacity 0.12s",
                        "&:hover": { opacity: dim ? 0.5 : 0.85 },
                      }}
                    />
                  </Tooltip>
                );
              })}
        </Box>

        {/* Legend chips — click to filter; the active one is tinted. */}
        <Flx ac g={1} sx={{ mt: 0.25, px: 0.25, flexWrap: "wrap" }}>
          {present.map((s) => {
            const active = activeStatus === s.key;
            return (
              <Box
                key={s.key}
                component="button"
                type="button"
                onClick={() => toggle(s.key)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  border: active
                    ? `1px solid ${s.border}`
                    : "1px solid transparent",
                  background: active ? s.tint : "transparent",
                  borderRadius: "999px",
                  px: 0.9,
                  py: "2px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                  transition: "background 0.12s, border-color 0.12s",
                  "&:hover": { background: active ? s.tint : "#f1f5f9" },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: s.color,
                    flexShrink: 0,
                  }}
                />
                <Txt
                  sx={{
                    fontSize: 10,
                    color: active ? s.text : "#64748b",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.label}{" "}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: active ? s.text : "#334155",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {s.count}
                  </Box>
                </Txt>
              </Box>
            );
          })}
        </Flx>
      </Flx>
    </Flx>
  );
};

// Compact Validity counts shown inline in the DocumentValiditySection
// header, next to the chevron. Same flat-row treatment as
// DocumentOverviewStatusCountsRow.
const DocumentValidityCountsRow = ({ validityCounts }) => {
  return (
    <Flx ac g={0} wrap sx={{ flexShrink: 0 }}>
      <CountTile
        row
        label="Valid"
        count={validityCounts?.valid ?? 0}
        dotColor={green[600]}
      />
      <CountTile
        row
        label="Expiring Soon"
        count={validityCounts?.expiringSoon ?? 0}
        dotColor={orange[600]}
        accentColor={
          (validityCounts?.expiringSoon ?? 0) > 0 ? orange[800] : undefined
        }
      />
      <CountTile
        row
        label="Expired"
        count={validityCounts?.expired ?? 0}
        dotColor={red[600]}
        accentColor={(validityCounts?.expired ?? 0) > 0 ? red[700] : undefined}
      />
    </Flx>
  );
};

// Stronger (2px) separator between major widget sections so the three
// reading groups ("Overview" / "By status" / "Validity") stay distinct.
// eslint-disable-next-line no-unused-vars
const SectionDivider = () => (
  <Box
    sx={{
      width: "2px",
      alignSelf: "stretch",
      bgcolor: "#dde1e7",
    }}
  />
);

// Single tile used by DocumentCountsWidget. Vertical stack: small
// uppercase label on top, larger count below. Optional dot color (for
// status tiles), tooltip (for the Hidden tile), and muted styling
// (when the underlying value is hidden by the table settings).
const CountTile = ({
  label,
  count,
  dotColor,
  tooltip,
  muted,
  accentColor,
  row = false,
  trailingDivider,
}) => {
  const tile = (
    <Flx
      column={!row}
      ac
      g={0.4}
      sx={{
        px: 1.5,
        py: 1,
        minWidth: 84,
        cursor: tooltip ? "help" : "default",
        opacity: muted ? 0.7 : 1,
      }}
    >
      <Flx ac g={0.5}>
        {dotColor && (
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: dotColor,
              flexShrink: 0,
            }}
          />
        )}
        <Txt
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            color: grey[700],
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Txt>
      </Flx>
      <Txt
        sx={{
          fontSize: row ? "10px" : "18px",
          fontWeight: 700,
          lineHeight: 1.1,
          color: accentColor || "#1A1A19",
        }}
      >
        {count}
      </Txt>
    </Flx>
  );
  const wrapped = tooltip ? (
    <Tooltip title={tooltip} placement="top">
      {tile}
    </Tooltip>
  ) : (
    tile
  );
  return wrapped;
};

// Unused after the section restructure (SectionDivider replaced it),
// but kept in case a follow-up wants thin per-tile separators back.
// eslint-disable-next-line no-unused-vars
const Divider = ({ thick = false }) => (
  <Box
    sx={{
      width: thick ? "2px" : "1px",
      alignSelf: "stretch",
      bgcolor: thick ? "#dde1e7" : "#eeece5",
      mx: thick ? 0.25 : 0,
    }}
  />
);

// =================================================================
// DOCUMENT VALIDITY SECTION (improved UI replacement for the legacy
// LoanExpiringDocumentsRenderer card grid). Three vertically-stacked
// status groups (Expired → Expiring Soon → Valid), each a list of rows
// with a horizontal time-remaining progress bar. The business rules
// (which docs count, what "expiring soon" means, validity-day mapping)
// are imported from LoanExpiringDocumentsRenderer so both views stay in
// sync.
// =================================================================
const VALIDITY_GROUP_DEFS = [
  {
    key: "expired",
    label: "Expired",
    icon: CancelOutlined,
    color: red[700],
    bg: "#fff6f6",
    // bg: "#ffebee45",

    border: red[200],
    helper: "Past their effective-date + valid-for window.",
  },
  {
    key: "expiringSoon",
    label: "Expiring Soon",
    icon: WarningAmberOutlined,
    color: orange[800],
    bg: orange[50],
    border: orange[200],
    helper: `Within the next ${10} days.`,
  },
  {
    key: "valid",
    label: "Valid",
    icon: CheckCircleOutline,
    color: green[700],

    bg: "#f5faf5",

    // ----
    // bg: "#e8f5e945",
    // bg: "#e8f5e975",
    // bg: green[50],
    border: green[200],
    helper: "Effective and within the valid-for window.",
  },
];

const DOCUMENT_VALIDITY_EXPANDED_STORAGE_KEY = nsKey(
  "loanDocumentValiditySectionExpanded",
);

const DocumentValiditySection = ({
  docs,
  useModalFilePreview = false,
  relatedFiles,
  validityCounts,
  // When true, the section renders even with zero applicable docs.
  forceVisible = false,
  sx,
}) => {
  const dispatch = useDispatch();
  const loanId = useSelector((state) => state.loanDrilldown?._id);
  // Associations are stored *inverse* on the loan: each borrower /
  // subject property / entity carries an `associations.loanDocuments`
  // list. Pull those collections once so we can compute a forward map
  // keyed by doc._id below, instead of scanning per-card.
  const borrowers = useSelector((state) => state.loanDrilldown?.borrowers);
  const subjectProperties = useSelector(
    (state) => state.loanDrilldown?.subjectProperties,
  );
  const loanEntities = useSelector((state) => state.loanDrilldown?.entities);
  const loanBorrowerEntity = useSelector(
    (state) => state.loanDrilldown?.borrowerEntity,
  );

  const [previewFile, setPreviewFile] = useState(null);

  const [editingFile, setEditingFile] = useState(null);

  const [sectionExpanded, setSectionExpanded] = useLocalStorageState(
    DOCUMENT_VALIDITY_EXPANDED_STORAGE_KEY,
    { defaultValue: true },
  );

  const grouped = useMemo(() => {
    const out = { expired: [], expiringSoon: [], valid: [] };
    for (const d of docs || []) {
      if (out[d.status]) out[d.status].push(d);
    }
    // Sort each group by remaining days ascending so the most urgent
    // sit at the top of every list.
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => a.remainingDays - b.remainingDays);
    }
    return out;
  }, [docs]);

  const associationsByDocId = useMemo(() => {
    const map = new Map();
    const ensure = (docId) => {
      if (!docId) return null;
      let entry = map.get(docId);
      if (!entry) {
        entry = { borrowers: [], subjectProperties: [], entities: [] };
        map.set(docId, entry);
      }
      return entry;
    };
    const scanArray = (key, arr) => {
      if (!Array.isArray(arr)) return;
      for (const item of arr) {
        const docIds = item?.associations?.loanDocuments;
        if (!Array.isArray(docIds)) continue;
        for (const docId of docIds) {
          const entry = ensure(docId);
          if (entry) entry[key].push(item);
        }
      }
    };
    scanArray("borrowers", borrowers);
    scanArray("subjectProperties", subjectProperties);
    if (Array.isArray(loanEntities)) {
      scanArray("entities", loanEntities);
    } else if (loanBorrowerEntity) {
      const docIds = loanBorrowerEntity?.associations?.loanDocuments;
      if (Array.isArray(docIds)) {
        for (const docId of docIds) {
          const entry = ensure(docId);
          if (entry) entry.entities.push(loanBorrowerEntity);
        }
      }
    }
    return map;
  }, [borrowers, subjectProperties, loanEntities, loanBorrowerEntity]);

  // Hide the validity chart entirely when there are no VALID documents (dated
  // docs that are all expired/expiring aren't worth a "validity" widget).
  if (
    (!docs || docs.length === 0 || grouped.valid.length === 0) &&
    !forceVisible
  )
    return null;
  const docCount = docs?.length || 0;

  // Open the file the same way the table does: route through the
  // drilldown sidebar by default, otherwise pop the inline
  // LoanFilePreviewModal when the user has opted into modal previews.
  const handleOpenFile = (doc) => {
    if (!useModalFilePreview) {
      dispatch(
        sidebarSetValues({
          type: loanDrilldownSidebarTypes.FILE_PREVIEW,
          state: doc,
        }),
      );
      return;
    }
    setPreviewFile(doc);
  };

  const handleEditFile = (doc) => {
    if (!doc) return;
    setEditingFile(doc);
  };

  // const ChevronIcon = sectionExpanded ? KeyboardArrowUp : KeyboardArrowDown;
  const ChevronIcon = sectionExpanded
    ? KeyboardArrowDown
    : ChevronRightOutlined;
  return (
    <Box
      sx={{
        // my: 3,
        background: "#ffffff",
        // background: grey[50],
        borderRadius: "8px",
        overflow: "hidden",
        // py: sectionExpanded ? 3 : 0,
        // borderTop: "1px solid #e5e3df",
        border: "1px solid #e5e3df",
        // borderBottom: "1px solid #e5e3df",
        // borderBottom: "1px solid #e5e3df",
        mb: 2,
        ...sx,
      }}
    >
      {/* Title row mirrors the "Uploaded Loan Documents" header above the
          document table — bold title + muted subtitle. Clicking anywhere
          on the row toggles the section's collapse state (persisted to
          local storage so the preference sticks). */}
      <Box
        onClick={() => setSectionExpanded((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          // gap: 1,

          // mb: 1,
          px: 1,
          // py: 2,
          background: "#f9faf9",
          // py: sectionExpanded ? 2 : 1.25,

          cursor: "pointer",
          userSelect: "none",
          transition: "background 150ms",
          "&:hover": {
            background: "#f9faf9a1",
            ".doc-validity-chevron": { color: grey[800] },
          },
        }}
      >
        <Flx g={0.4} ac sx={{ py: 1 }}>
          <ChevronIcon
            className="doc-validity-chevron"
            sx={{ fontSize: 22, color: grey[600] }}
          />
          <Flx
            ac
            g={1}
            //
            // column
          >
            {/* <HourglassEmptyOutlined sx={{ fontSize: 16, mr: 0.4 }} /> */}
            <Txt
              sx={{
                // fontStyle: "italic",
                //
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              Document Validity ({docCount})
            </Txt>
            <Txt
              // fontSize={11}
              sx={{
                fontSize: "11px",
                color: grey[600],
                ml: 1,
                lineHeight: 1,
                fontStyle: "italic",
              }}
            >
              Files with an effective date set. Click a status group to expand.
            </Txt>
          </Flx>
        </Flx>

        <DocumentValidityCountsRow validityCounts={validityCounts} />
      </Box>

      <Collapse in={sectionExpanded} timeout={180} unmountOnExit>
        <Flx
          column
          g={1}
          sx={{
            p: 1,
            pt: 0.25,
            // background: "#ffffff",
            // borderTop: "1px solid #e5e3df",
            // mt: 0,
            // border: `1px solid ${yellow[300]} `,
          }}
        >
          {docCount === 0 ? (
            <Txt secondary sx={{ fontSize: 12, fontStyle: "italic" }}>
              No documents with an effective date yet. Set a report effective
              date on a file (Validity / Expiration Duration) and it'll show up
              here with its valid / expiring-soon / expired status.
            </Txt>
          ) : (
            VALIDITY_GROUP_DEFS.map((g) => {
              const list = grouped[g.key] || [];
              if (list.length === 0) return null;
              return (
                <ValidityGroup
                  key={g.key}
                  groupDef={g}
                  docs={list}
                  onOpenFile={handleOpenFile}
                  onEditFile={handleEditFile}
                  associationsByDocId={associationsByDocId}
                />
              );
            })
          )}
        </Flx>
      </Collapse>

      {/* Inline file-preview modal, same component the table uses for the
          non-sidebar path. Only mounts content when `open` is true, so the
          cost is zero when no file is being previewed. `relatedFiles` is
          the full loan-documents list so prev/next navigation isn't limited
          to validity docs. */}
      <LoanFilePreviewModal
        file={previewFile}
        open={!!previewFile}
        containerId={previewFile?.containerName || previewFile?.dealId}
        relatedFiles={relatedFiles || docs}
        onClose={() => setPreviewFile(null)}
        onSelectRelatedFile={setPreviewFile}
      />
      {/* Same Edit Details modal the document table opens from its
          right-click menu — keeps the editing flow consistent across the
          two surfaces. */}
      <EditDocumentDetailsDialog
        open={!!editingFile}
        file={editingFile}
        loanId={loanId}
        onClose={() => setEditingFile(null)}
        onSaved={() => setEditingFile(null)}
      />
    </Box>
  );
};

const ValidityGroup = ({
  groupDef,
  docs,
  onOpenFile,
  onEditFile,
  associationsByDocId,
}) => {
  const Icon = groupDef.icon;
  // Each status group is independently collapsible, collapsed by default
  // — keeps the section compact above the doc table; users open the
  // categories they care about.
  const [expanded, setExpanded] = useState(false);
  const ChevronIcon = expanded ? KeyboardArrowDown : ChevronRightOutlined;
  // const ChevronIcon = expanded ? KeyboardArrowUp : KeyboardArrowDown;
  return (
    <Box
      sx={{
        border: `1px solid ${groupDef.border}`,
        borderRadius: "8px",
        overflow: "hidden",
        flexGrow: 1,
        // bgcolor: "#fff",
        bgcolor: groupDef.bg,
      }}
    >
      {/* Plain Box (not Flx) — Flx doesn't forward onClick to its
          underlying div, so the toggle handler would be dropped. */}
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          pl: 1,
          // bgcolor: "#ffffff",
          bgcolor: groupDef.bg,
          // borderBottom: expanded ? `1px solid ${groupDef.border}` : "none",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { filter: "brightness(0.98)" },
        }}
      >
        <ChevronIcon sx={{ fontSize: 20, color: groupDef.color }} />
        <Flx ac g={0.5}>
          <Icon sx={{ fontSize: 13, color: groupDef.color }} />
          <Flx ac g={0.25}>
            <Txt
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: groupDef.color,
              }}
            >
              {groupDef.label}
            </Txt>
            <Txt
              sx={{
                fontSize: "12px",
                color: groupDef.color,
                opacity: 0.85,
                lineHeight: 1,
              }}
            >
              ({docs.length})
            </Txt>
          </Flx>
        </Flx>
        <Txt
          sx={{
            fontSize: "11px",
            color: grey[600],
            ml: 1,
            lineHeight: 1,
            fontStyle: "italic",
          }}
        >
          {groupDef.helper}
        </Txt>
        <Box sx={{ flex: 1 }} />
      </Box>
      <Collapse in={expanded} timeout={180} unmountOnExit>
        {/* Responsive card grid — each doc renders as its own tile.
            `minmax(280px, 1fr)` keeps cards readable while letting the
            grid spread across the available width (1 column on narrow
            screens, more as the container grows). Easy to revert to a
            single-column list by swapping for a Flx column. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            // gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 1.25,
            p: 1.5,
            pt: 0.5,
          }}
        >
          {docs.map((doc, idx) => (
            <ValidityRow
              key={doc._id || `${groupDef.key}-${idx}`}
              doc={doc}
              groupDef={groupDef}
              onOpenFile={onOpenFile}
              onEditFile={onEditFile}
              associations={associationsByDocId?.get(doc._id)}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const ValidityRow = ({
  doc,
  groupDef,
  onOpenFile,
  onEditFile,
  associations,
}) => {
  const expDaysLabel =
    doc.expirationDays == null
      ? "Unknown"
      : doc.expirationDays === "YEAR_END"
        ? "Year End"
        : `${doc.expirationDays} days`;
  const effectiveLabel = formatDate(doc.effective, "—", "M/d/yyyy");
  const expirationLabel = formatDate(doc.expiration, "—", "M/d/yyyy");
  const remainingLabel =
    doc.remainingDays == null
      ? "—"
      : doc.remainingDays < 0
        ? `${Math.abs(doc.remainingDays)} day${Math.abs(doc.remainingDays) === 1 ? "" : "s"} ago`
        : `${doc.remainingDays} day${doc.remainingDays === 1 ? "" : "s"} left`;
  // Hidden treatment mirrors the document table — amber tint, dashed
  // visibility-off badge, italic filename. Slightly different from the
  // row layout: applied to the whole card and the badge sits in the
  // header row.
  const hidden = isDocHidden(doc);

  return (
    // Card container. Plain Box (not Flx) so onClick reaches the DOM.
    <Box
      onClick={() => onOpenFile(doc)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 1.5,
        borderRadius: "6px",
        border: `1px solid ${hidden ? "#facc15" : groupDef.border}`,
        bgcolor: hidden ? "#fef9c3" : "#fff",
        cursor: "pointer",
        transition: "transform 120ms ease, box-shadow 120ms ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px -6px rgba(15, 23, 42, 0.18)",
        },
      }}
    >
      {/* Header — doc identity + edit affordance */}
      <Box sx={{ minWidth: 0 }}>
        <Flx ac g={0.75} sx={{ minWidth: 0, mb: 0.25 }}>
          {hidden && (
            <Tooltip title="This file is marked hidden" placement="top">
              <Flx
                ac
                center
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "4px",
                  bgcolor: grey[300],
                  border: `1px dashed ${grey[500]}`,
                  color: grey[700],
                  flexShrink: 0,
                }}
              >
                <VisibilityOffOutlined sx={{ fontSize: "13px !important" }} />
              </Flx>
            </Tooltip>
          )}
          <Txt
            sx={{
              fontSize: "13px",
              fontWeight: 700,
              color: hidden ? grey[700] : grey[900],
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
              flex: 1,
              minWidth: 0,
            }}
          >
            {doc.docType || "Untyped"}
          </Txt>
          {onEditFile && (
            <Tooltip title="Edit document details" placement="top">
              <IconButton
                size="small"
                // stopPropagation so the card's click handler (file
                // preview) doesn't also fire when the user clicks edit.
                onClick={(e) => {
                  e.stopPropagation();
                  onEditFile(doc);
                }}
                sx={{
                  p: 0.25,
                  flexShrink: 0,
                  color: grey[600],
                  "&:hover": {
                    bgcolor: grey[100],
                    color: grey[800],
                  },
                }}
              >
                <EditOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Flx>
        <Txt
          sx={{
            fontSize: "10px",
            color: grey[600],
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
            fontStyle: hidden ? "italic" : "normal",
          }}
        >
          {doc.file_display_name}
        </Txt>
      </Box>

      {/* Dates + valid-for */}
      <Flx ac jb g={0.5} sx={{ mt: 0.25 }}>
        <Txt sx={{ fontSize: "10px", color: grey[600] }}>
          {effectiveLabel} → {expirationLabel}
        </Txt>
        <Txt sx={{ fontSize: "10px", color: grey[500] }}>{expDaysLabel}</Txt>
      </Flx>

      {/* Progress bar */}
      <ValidityProgressBar
        percent={doc.percentRemaining}
        color={groupDef.color}
        bg={groupDef.bg}
        expired={doc.status === "expired"}
      />

      {/* Footer — days-remaining pill on the left, association icons
          (borrowers / properties / entities) on the right when this doc
          is linked to anything. Mirrors the AssociationIcons row in the
          document table's filename cell. */}
      <Flx ac jb g={0.75} sx={{ mt: 0.25, flexWrap: "wrap" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 0.9,
            py: 0.3,
            borderRadius: "999px",
            bgcolor: groupDef.bg,
            border: `1px solid ${groupDef.border}`,
          }}
        >
          <Txt
            sx={{
              fontSize: "10px",
              fontWeight: 700,
              color: groupDef.color,
              whiteSpace: "nowrap",
              letterSpacing: "0.2px",
            }}
          >
            {remainingLabel}
          </Txt>
        </Box>
        <ValidityRowAssociations associations={associations} />
      </Flx>
    </Box>
  );
};

// Resolves a friendly display label for each associatable type. Kept
// short on purpose — the chip caps its width and ellipses long values.
const formatBorrowerLabel = (b) => {
  const first = (b?.firstName || "").trim();
  const last = (b?.lastName || "").trim();
  const joined = `${first} ${last}`.trim();
  return joined || b?.email || "Borrower";
};
const formatPropertyLabel = (p) =>
  addressLabel(p?.address) ||
  p?.address?.street ||
  p?.propertyName ||
  "Property";
const formatEntityLabel = (e) => e?.entityName || e?.name || "Entity";

// Renders each linked borrower / subject property / entity as a chip.
// Clicking a borrower or property chip opens its detail sidebar; entity
// chips are display-only since there is no entity detail sidebar
// content type. Chips cap their width and ellipse long names.
const ValidityRowAssociations = ({ associations }) => {
  const dispatch = useDispatch();
  const borrowers = associations?.borrowers || [];
  const properties = associations?.subjectProperties || [];
  const entities = associations?.entities || [];
  if (!borrowers.length && !properties.length && !entities.length) return null;

  const handleOpenBorrower = (b) => (e) => {
    e.stopPropagation();
    if (!b) return;
    dispatch(
      detailSidebarOpen(DETAIL_SIDEBAR_CONTENT_TYPES.BORROWER, b, b?._id, {
        title: formatBorrowerLabel(b),
        mode: "view",
        source: "DocumentValiditySection",
        sourceField: "borrowers",
      }),
    );
  };
  const handleOpenProperty = (p) => (e) => {
    e.stopPropagation();
    if (!p) return;
    dispatch(
      detailSidebarOpen(
        DETAIL_SIDEBAR_CONTENT_TYPES.SUBJECT_PROPERTY,
        p,
        p?._id,
        {
          title: formatPropertyLabel(p),
          mode: "view",
          source: "DocumentValiditySection",
          sourceField: "subjectProperties",
        },
      ),
    );
  };

  const chipSx = {
    height: 22,
    maxWidth: 160,
    borderRadius: "6px",
    cursor: "pointer",
    ".MuiChip-icon": {
      fontSize: "13px !important",
      marginLeft: "6px !important",
      marginRight: "-2px !important",
    },
    ".MuiChip-label": {
      fontSize: "11px",
      fontWeight: 600,
      px: 0.75,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };
  const staticChipSx = { ...chipSx, cursor: "default" };

  return (
    <Flx ac g={0.5} sx={{ flexWrap: "wrap" }}>
      {borrowers.map((b) => {
        const label = formatBorrowerLabel(b);
        return (
          <Tooltip key={b?._id || label} title={label}>
            <Chip
              size="small"
              icon={<PersonOutline />}
              label={label}
              onClick={handleOpenBorrower(b)}
              sx={{
                ...chipSx,
                bgcolor: "#ecfdf5",
                color: "#047857",
                border: "1px solid #a7f3d0",
                "&:hover": { bgcolor: "#d1fae5" },
                ".MuiChip-icon": {
                  ...chipSx[".MuiChip-icon"],
                  color: "#047857 !important",
                },
              }}
            />
          </Tooltip>
        );
      })}
      {properties.map((p) => {
        const label = formatPropertyLabel(p);
        return (
          <Tooltip key={p?._id || label} title={label}>
            <Chip
              size="small"
              icon={<HouseOutlined />}
              label={label}
              onClick={handleOpenProperty(p)}
              sx={{
                ...chipSx,
                bgcolor: "#fffbeb",
                color: "#b45309",
                border: "1px solid #fde68a",
                "&:hover": { bgcolor: "#fef3c7" },
                ".MuiChip-icon": {
                  ...chipSx[".MuiChip-icon"],
                  color: "#b45309 !important",
                },
              }}
            />
          </Tooltip>
        );
      })}
      {entities.map((en) => {
        const label = formatEntityLabel(en);
        return (
          <Tooltip key={en?._id || label} title={label}>
            <Chip
              size="small"
              icon={<ApartmentOutlined />}
              label={label}
              sx={{
                ...staticChipSx,
                bgcolor: "#faf5ff",
                color: "#7e22ce",
                border: "1px solid #e9d5ff",
                ".MuiChip-icon": {
                  ...chipSx[".MuiChip-icon"],
                  color: "#7e22ce !important",
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Flx>
  );
};

const ValidityProgressBar = ({ percent, color, bg, expired }) => {
  // Expired rows render a flat fully-filled red bar so they read as
  // "no remaining time at all" rather than a 0% sliver of green.
  const fillPercent = expired ? 100 : Math.max(2, Math.min(100, percent));
  return (
    <Box
      sx={{
        height: 6,
        bgcolor: bg,
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: "100%",
          width: `${fillPercent}%`,
          bgcolor: color,
          opacity: expired ? 0.9 : 1,
          transition: "width 240ms ease",
        }}
      />
    </Box>
  );
};

// Drop-in replacement for LoanDocumentUploaderProcess.
// Two changes from the previous version:
//   1. Queue section is wrapped in a distinct card (background, border,
//      rounded corners) so it reads as "Step 2: Review" rather than as a
//      continuation of the doc-group grid.
//   2. The action bar (Clear All / Edit / Upload) is pulled out into a
//      sticky footer that pins to the bottom of the viewport while the
//      queue is visible. Long queues no longer require scrolling all the
//      way down to hit Upload.

// Exported so the reusable LoanDocumentUploadModal can render the exact same
// two upload views (Quick Upload dropzone / Upload By Category rail) inside a
// dialog. Must be used within an UploadQueueProvider.
export const LoanDocumentUploaderProcess = ({
  view,
  paneLayout = false,
  minCardWidth,
}) => {
  // Queue state now lives in UploadQueueContext so files dropped here AND on
  // Missing-documents rows land in the same fixed review panel below.
  const { addedFiles, onFilesAdded, onRemoveFile } = useUploadQueue();

  return (
    <Box
      sx={{
        position: "relative",
        // NOTE: keep this "visible" — an overflow:hidden/clip ancestor turns
        // into a scroll container and breaks position:sticky on the left
        // category rail below.
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        // In the modal (paneLayout) the By-Category view is a full-height,
        // two-pane layout that owns its scroll — so fill the parent's height.
        ...(paneLayout && { flex: 1, minHeight: 0 }),
      }}
    >
      {view === UPLOAD_TYPES.QUICK_UPLOAD &&
        (paneLayout ? (
          // Modal keeps the tall dropzone.
          <FileUploadField
            key={""}
            onFilesAdded={onFilesAdded}
            suppressGrid
            borderSize={3}
            borderColor="#d4d9e0"
            suppressFileList
            borderRadius={"16px"}
            minHeight={"120px"}
            suppressUploadButton
          />
        ) : (
          // Documents tab: a slim single-line drop bar.
          <FileUploadField
            key={""}
            onFilesAdded={onFilesAdded}
            suppressGrid
            suppressFileList
            suppressUploadButton
            borderSize={2}
            borderColor="#dbe0e6"
            background="#ffffff"
            borderRadius={"10px"}
            height="auto"
            minHeight="auto"
            uploadTextFontSize="12.5px"
            uploadTextDefaultOpacity={1}
            uploadText={
              <>
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: "#334155" }}
                >
                  Drop files here
                </Box>
                <Box component="span" sx={{ color: "#94a3b8" }}>
                  {" "}
                  — or click to browse. Files land in the queue below for typing
                  &amp; assignment.
                </Box>
              </>
            }
            uploadTextSx={{ textAlign: "left" }}
            sx={{
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              textAlign: "left",
              gap: 1.25,
              px: 2,
              py: 1.25,
            }}
          />
        ))}
      {view === UPLOAD_TYPES.GROUP && (
        <UploadLoanDocumentByGroupProcess
          addedFiles={addedFiles}
          onFilesAdded={onFilesAdded}
          onRemoveFile={onRemoveFile}
          queueContent={null}
          minCardWidth={minCardWidth}
          paneLayout={paneLayout}
        />
      )}
    </Box>
  );
};

// Fixed, bottom-docked review panel. Drains the shared upload queue: shows the
// staged files, a bulk-edit panel, and the Discard / Upload actions in its
// header. Collapsible, with a scrollable body capped to a fraction of the
// viewport so it never eats the whole screen on small displays.
// Exported so the Entity tab's documents view can reuse the same bottom-docked
// queue (fed from its per-docType drop cards).
//
// `hideAssignmentPicker` suppresses the "Assign to" pill row (used by the
// reusable upload modal when it's opened locked to a specific borrower/property
// — the target is already fixed via UploadQueueProvider.initialAssociationTarget).
// `assignmentLabel`, when set, renders a small read-only "Assigning to <label>"
// indicator in its place.
export const FixedUploadQueue = ({
  hideAssignmentPicker = false,
  assignmentLabel = null,
} = {}) => {
  const {
    addedFiles,
    setAddedFiles,
    selectedFiles,
    setSelectedFiles,
    bulkEffectiveDate,
    setBulkEffectiveDate,
    bulkDuration,
    setBulkDuration,
    onDocGroupTypeChange,
    onBulkEffectiveDateChange,
    onBulkDurationChange,
    handleUploadFiles,
    clearFiles,
    loading,
    associationTarget,
    setAssociationTarget,
  } = useUploadQueue();
  const [collapsed, setCollapsed] = useState(false);
  // Local echo of what the bulk Document Type picker last applied. The bulk
  // control is a write-only fan-out (it stamps every selected file), so it has
  // no single source of truth to read back from — this just keeps the field
  // showing what you picked instead of snapping back to the placeholder.
  const [bulkDocSelection, setBulkDocSelection] = useState({
    docGroup: null,
    docType: null,
  });
  const borrowers = useSelector((s) => s.loanDrilldown?.borrowers) || [];
  const properties =
    useSelector((s) => s.loanDrilldown?.subjectProperties) || [];

  if (isEmpty(addedFiles)) return null;

  // Minimized: a compact, fully-rounded pill floating off the bottom. Shows
  // only the queued count; click (or the chevron) expands the full panel.
  if (collapsed) {
    return (
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 20,
          zIndex: 1250,
          px: 2,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <Flx
          ac
          g={1.25}
          onClick={() => setCollapsed(false)}
          sx={{
            pointerEvents: "auto",
            cursor: "pointer",
            background: "#0d9488",
            color: "#ffffff",
            borderRadius: "999px",
            pl: 1.75,
            pr: 1,
            py: 0.75,
            boxShadow: "0 12px 30px -8px rgba(15, 23, 42, 0.5)",
            transition: "background 120ms",
            "&:hover": { background: "#254b60" },
          }}
        >
          <ContentPasteGoOutlined sx={{ fontSize: 18, flexShrink: 0 }} />
          <Txt sx={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>
            {size(addedFiles)} file{size(addedFiles) === 1 ? "" : "s"} queued
          </Txt>
          <Flx
            center
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.12)",
            }}
          >
            <KeyboardArrowUp sx={{ fontSize: 18 }} />
          </Flx>
        </Flx>
      </Box>
    );
  }

  // Association options: "Directly to Loan" first, then every borrower and
  // property on the loan. Hidden entirely when the loan has neither.
  const hasAssociatables = borrowers.length > 0 || properties.length > 0;
  const assocOptions = [
    {
      key: "loan",
      type: "loan",
      id: null,
      icon: ContentPasteGoOutlined,
      title: "Directly to Loan",
      subtitle: "Upload without assigning",
    },
    ...borrowers.map((b) => ({
      key: `borrower-${b?._id}`,
      type: "borrower",
      id: b?._id,
      icon: PersonOutline,
      title:
        [b?.firstName, b?.lastName].filter(Boolean).join(" ") || "Borrower",
      subtitle: "Borrower",
    })),
    ...properties.map((p) => ({
      key: `property-${p?._id}`,
      type: "property",
      id: p?._id,
      icon: HouseOutlined,
      title: addressLabel(p?.address) || p?.address?.street || "Property",
      subtitle: "Property",
    })),
  ];
  const isAssocSelected = (opt) =>
    opt.type === "loan"
      ? !associationTarget
      : associationTarget?.type === opt.type &&
        associationTarget?.id === opt.id;

  // A queued file "needs a start date" when its type carries a validity window
  // (auto or set) but no effective start date has been entered yet.
  const needsStartDate = (f) => {
    const days =
      f?.reportGuidelineExpirationDays ??
      getExpirationDaysForDocType(f?.docType);
    return !!days && !f?.reportEffectiveDate;
  };
  const startDateIssues = addedFiles.filter(needsStartDate).length;

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1250,
        px: 2,
        pr: "64px",
        display: "flex",
        justifyContent: "center",
        // Let clicks pass through the gutter around the card.
        pointerEvents: "none",
      }}
    >
      <Card
        sx={{
          pointerEvents: "auto",
          width: "100%",
          maxWidth: 1480,
          // maxWidth: 1080,
          p: 0,
          borderRadius: "12px 12px 0 0",
          border: "1px solid #e2e8f0",
          // border: "1px solid #e2e8f0",
          borderBottom: "none",
          background: "#ffffff",
          // Heavier-but-soft lift so the panel reads as floating above the page.
          boxShadow:
            "0 -2px 8px -4px rgba(15, 23, 42, 0.12), 0 -16px 40px -12px rgba(15, 23, 42, 0.28)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "min(70vh, 640px)",
        }}
      >
        {/* Header — dark teal bar: queued count + a readiness / "needs a start
            date" hint, plus Discard / Upload and the collapse chevron. */}
        <Flx
          ac
          g={1.5}
          sx={{
            px: 2,
            py: 1.25,
            background: "#0f5249",
            flexShrink: 0,
          }}
        >
          <FileUploadOutlined
            sx={{ fontSize: 20, color: "#ffffff", flexShrink: 0 }}
          />
          <Flx ac wrap g={1} sx={{ minWidth: 0, flex: 1 }}>
            <Txt
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: "#ffffff",
                lineHeight: 1.5,
              }}
            >
              {size(addedFiles)} file{size(addedFiles) === 1 ? "" : "s"} queued
            </Txt>
            {startDateIssues > 0 ? (
              <Flx ac g={0.5}>
                <WarningAmberOutlined sx={{ fontSize: 13, color: "#fbbf24" }} />
                <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: "#fbbf24" }}>
                  {startDateIssues} need{startDateIssues === 1 ? "s" : ""} a
                  start date
                </Txt>
              </Flx>
            ) : (
              <Txt
                sx={{
                  fontSize: 11.5,
                  color: "rgba(255,255,255,0.65)",
                  lineHeight: 1.4,
                }}
              >
                Ready to upload
              </Txt>
            )}
          </Flx>
          <Button
            onClick={clearFiles}
            variant="text"
            sx={{
              textTransform: "none",
              fontSize: 12.5,
              color: "rgba(255,255,255,0.85)",
              "&:hover": {
                background: "rgba(255,255,255,0.12)",
                color: "#ffffff",
              },
            }}
          >
            Discard all
          </Button>
          <Button
            loading={loading}
            onClick={handleUploadFiles}
            startIcon={<FileUploadOutlined />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12.5,
              px: 2.25,
              // Teal CTA on the dark teal bar — set bgcolor AND color together
              // so the white label stays readable (theme default Button is navy).
              bgcolor: "#0d9488",
              color: "#ffffff",
              boxShadow: "none",
              "&:hover": { bgcolor: "#14b8a6", boxShadow: "none" },
            }}
          >
            Upload {size(addedFiles)}{" "}
            {size(addedFiles) === 1 ? "file" : "files"}
          </Button>
          <Tooltip title={collapsed ? "Expand" : "Collapse"} arrow>
            <IconButton
              onClick={() => setCollapsed((v) => !v)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.8)",
                ml: 0.5,
                "&:hover": { background: "rgba(255,255,255,0.12)" },
              }}
            >
              {collapsed ? (
                <KeyboardArrowUp sx={{ fontSize: 22 }} />
              ) : (
                <KeyboardArrowDown sx={{ fontSize: 22 }} />
              )}
            </IconButton>
          </Tooltip>
        </Flx>

        {/* Locked-target indicator — shown when the queue is opened pre-assigned
            to a specific borrower/property (the reusable upload modal). */}
        {hideAssignmentPicker && assignmentLabel && (
          <Flx
            ac
            g={1}
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.25,
              borderBottom: "1px solid #eef1f6",
            }}
          >
            <Txt
              sx={{
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 600,
                color: "#64748b",
              }}
            >
              Assigning to
            </Txt>
            <Flx
              ac
              g={0.75}
              sx={{
                borderRadius: "999px",
                border: "1px solid #0d94898c",
                background: "#0d948915",
                color: "#0d9488",
                px: 1.25,
                py: 0.6,
              }}
            >
              <Txt sx={{ fontSize: 12, fontWeight: 600, color: "inherit" }}>
                {assignmentLabel}
              </Txt>
            </Flx>
          </Flx>
        )}

        {/* Association picker — pill row to assign the queued docs to the loan,
            a borrower, or a property. Hidden when the loan has neither, or when
            the target is locked by the caller (modal). */}
        {!hideAssignmentPicker && hasAssociatables && (
          <Flx
            ac
            g={1}
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.25,
              borderBottom: "1px solid #eef1f6",
              overflowX: "auto",
            }}
          >
            <Txt
              sx={{
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 600,
                color: "#64748b",
              }}
            >
              Assign to
            </Txt>
            <Flx g={0.75} sx={{ flexShrink: 0 }}>
              {assocOptions.map((opt) => {
                const selected = isAssocSelected(opt);
                const Icon = opt.icon;
                return (
                  <Flx
                    key={opt.key}
                    ac
                    g={0.75}
                    onClick={() =>
                      setAssociationTarget(
                        opt.type === "loan"
                          ? null
                          : { type: opt.type, id: opt.id },
                      )
                    }
                    sx={{
                      flexShrink: 0,
                      cursor: "pointer",
                      borderRadius: "999px",
                      border: "1px solid",
                      borderColor: selected ? "#0d94898c" : "#e2e8f0",
                      // background: selected ? "#0d9488" : "#ffffff",
                      // color: selected ? "#ffffff" : "#475569",
                      background: selected ? "#0d948915" : "#ffffff",
                      color: selected ? "#0d9488" : "#475569",
                      px: 1.25,
                      py: 0.6,
                      whiteSpace: "nowrap",
                      transition: "all 120ms",
                      "&:hover": {
                        borderColor: selected ? "#0d9488" : "#cbd5e1",
                        background: selected ? "#0d94892d" : "#f8fafc",
                      },
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 14,
                        color: selected ? "#0d9488" : "#94a3b8",
                      }}
                    />
                    <Txt
                      sx={{ fontSize: 12, fontWeight: 600, color: "inherit" }}
                    >
                      {opt.title}
                    </Txt>
                    <Txt
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        // color: selected ? "#0d9489c2" : "#94a3b8",
                        color: selected ? "#0d9489d3" : "#94a3b8",
                        // color: selected ? "#0d9489bf" : "#94a3b8",
                      }}
                    >
                      · {opt.subtitle}
                    </Txt>
                  </Flx>
                );
              })}
            </Flx>
          </Flx>
        )}

        {/* Body — scrollable, capped so it never eats the whole viewport. */}
        {!collapsed && (
          <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
            <Flx column>
              <UploadedFilesTable
                files={addedFiles}
                setFiles={setAddedFiles}
                setSelectedFiles={setSelectedFiles}
              />
              {!isEmpty(selectedFiles) && (
                <Box sx={{ p: 2, borderTop: "1px solid #eef1f6" }}>
                  <Txt
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#0f172a",
                      mb: 1.5,
                    }}
                  >
                    Bulk edit ({size(selectedFiles)} selected file
                    {size(selectedFiles) === 1 ? "" : "s"})
                  </Txt>
                  <Flx g={2} wrap sx={{ alignItems: "flex-start" }}>
                    <Box sx={{ flex: "1 1 260px", minWidth: 220 }}>
                      <InputWrapper label="Document Type">
                        {/* Trial of the modal doc-type picker (the taxonomy is
                            ~190 types / 20 groups — too much for a dropdown).
                            Same { docGroup, docType } onChange contract as
                            SelectFileAssignmentInput, so both still update. */}
                        <DocTypePickerField
                          // small = 36px, the rendered height of the date /
                          // duration TextFields beside it (MUI size="small"
                          // with this panel's 13px input font override).
                          size="small"
                          docGroup={bulkDocSelection.docGroup}
                          docType={bulkDocSelection.docType}
                          // No clear affordance: the bulk apply can't be
                          // un-applied (onDocGroupTypeChange ignores an empty
                          // payload), so a ✕ here would lie about the files.
                          allowClear={false}
                          suggestFrom={selectedFiles.map(
                            (f) => f?.file_display_name || f?.name,
                          )}
                          subjectLabel={`${size(selectedFiles)} selected file${
                            size(selectedFiles) === 1 ? "" : "s"
                          }`}
                          onChange={(p) => {
                            setBulkDocSelection({
                              docGroup: p?.docGroup || null,
                              docType: p?.docType || null,
                            });
                            onDocGroupTypeChange(p);
                          }}
                        />
                      </InputWrapper>
                    </Box>
                    <Box sx={{ flex: "0 0 190px" }}>
                      <InputWrapper label="Effective Start Date">
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={bulkEffectiveDate}
                          onChange={(e) => {
                            setBulkEffectiveDate(e.target.value);
                            onBulkEffectiveDateChange(e.target.value);
                          }}
                          sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
                        />
                      </InputWrapper>
                    </Box>
                    <Box sx={{ flex: "0 0 170px" }}>
                      <InputWrapper label="Effective Duration">
                        <Flx ac g={0.75}>
                          <TextField
                            type="number"
                            size="small"
                            placeholder="—"
                            value={bulkDuration}
                            onChange={(e) => setBulkDuration(e.target.value)}
                            onBlur={() => onBulkDurationChange(bulkDuration)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.currentTarget.blur();
                            }}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{
                              width: 80,
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                textAlign: "center",
                              },
                            }}
                          />
                          <Txt sx={{ fontSize: 12, color: grey[500] }}>
                            days
                          </Txt>
                        </Flx>
                      </InputWrapper>
                    </Box>
                  </Flx>
                </Box>
              )}
            </Flx>
          </Box>
        )}
      </Card>
    </Box>
  );
};
// =====================================================================
// Upload-queue cell renderers (doc type / effective date / expiration)
// =====================================================================
//
// Render interactive inputs INSIDE the staged-files grid so each queued
// file carries its own doc-type dropdown, effective-date picker, and a
// computed expiration readout — replacing the old "set type for selected
// files" popover. Each wrapper stops click propagation so interacting with
// the input never triggers AG Grid row selection or the row's
// delete/parse cell-click handlers.

const UploadQueueDocTypeCell = ({ data, onChange }) => (
  <Box
    onClick={(e) => e.stopPropagation()}
    sx={{ width: "100%", "& .MuiInputBase-input": { fontSize: 13 } }}
  >
    <SelectFileAssignmentInput
      onlyDisplayDocType
      size="small"
      placeholder="Select type"
      docGroup={data?.docGroup}
      docType={data?.docType}
      onChange={(p) => onChange(data?.id, p || {})}
    />
  </Box>
);

// Native date input for the upload queue that avoids spurious commits. A real
// date selection commits immediately; CLEARING the field is only committed on
// blur. That way dismissing the picker / hitting the clear ✕ doesn't apply an
// empty value and re-sort the queue on every interaction. Draft state keeps it
// responsive and re-syncs from props while unfocused.
const QueueDateInput = ({ value, onCommit, InputProps, sx }) => {
  const [draft, setDraft] = useState(value || "");
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setDraft(value || "");
  }, [value]);
  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{ width: "100%" }}>
      <TextField
        type="date"
        size="small"
        fullWidth
        value={draft}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          // Selecting a date applies right away; clearing waits for blur so a
          // dismissed picker doesn't commit an empty value.
          if (v) onCommit(v);
        }}
        onBlur={() => {
          focusedRef.current = false;
          if ((draft || "") !== (value || "")) onCommit(draft);
        }}
        InputProps={InputProps}
        sx={sx}
      />
    </Box>
  );
};

const UploadQueueEffectiveDateCell = ({ data, onChange }) => {
  // Explicit-expiration types (license/passport) derive the effective date
  // from the picked expiration date — it isn't user-set here.
  if (isExplicitExpirationDocType(data?.docType)) {
    return <Txt sx={{ fontSize: 12, color: grey[400] }}>N/A</Txt>;
  }
  // A file with a validity duration expires `days` after its effective start
  // date, so it NEEDS a start date — but we deliberately don't default it to
  // today (these reports are almost never effective on the upload date).
  // Highlight instead. Keyed off the stored duration so a manually-entered
  // duration on an otherwise-unmapped type also triggers the prompt.
  const days =
    data?.reportGuidelineExpirationDays ??
    getExpirationDaysForDocType(data?.docType);
  const needsDate = !!days && !data?.reportEffectiveDate;
  return (
    <QueueDateInput
      value={(data?.reportEffectiveDate || "").slice(0, 10)}
      onCommit={(v) => onChange(data?.id, v)}
      InputProps={{
        startAdornment: needsDate ? (
          <Tooltip
            title={`${data?.docType} expires ${days} days after its effective date — enter the date the report states`}
          >
            <WarningAmberOutlined
              sx={{ fontSize: 16, color: amber[700], mr: 0.5 }}
            />
          </Tooltip>
        ) : null,
      }}
      sx={{
        "& .MuiInputBase-input": { fontSize: 13, py: 0.85 },
        ...(needsDate && {
          bgcolor: amber[50],
          "& .MuiOutlinedInput-notchedOutline": { borderColor: amber[400] },
        }),
      }}
    />
  );
};

const UploadQueueDurationCell = ({ data, onChange }) => {
  const docType = data?.docType;
  const value = data?.reportGuidelineExpirationDays;
  // Explicit-expiration (license/passport) and dual-date (statements) types
  // derive their duration from the picked dates — not user-meaningful here.
  if (isExplicitExpirationDocType(docType) || isDualDateDocType(docType)) {
    return <Txt sx={{ fontSize: 12, color: grey[400] }}>N/A</Txt>;
  }
  // Hard types carry a fixed window: show the auto pill (read-only), sourcing
  // the day count from the map so it always shows even if the file hasn't had
  // `reportGuidelineExpirationDays` stamped yet. Soft / questionable types
  // (and unmapped types) get an editable number input.
  if (isHardDurationDocType(docType)) {
    return <AutoDurationPill days={getExpirationDaysForDocType(docType)} />;
  }
  return <DurationInput rowId={data?.id} value={value} onChange={onChange} />;
};

// Editable duration input. Holds its OWN draft state and only commits to the
// parent (which mutates `files` and re-renders the grid) on blur / Enter —
// NOT on every keystroke. Committing per keystroke churned `rowData`, which
// refreshed the cell and dropped focus after the first digit, so double-digit
// entry was impossible. The draft syncs from props only while unfocused, so
// external resets (e.g. a doc-type change clearing the value) still flow in.
const DurationInput = ({ rowId, value, onChange }) => {
  const [draft, setDraft] = useState(value ?? "");
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setDraft(value ?? "");
  }, [value]);
  const commit = () => {
    focusedRef.current = false;
    onChange(rowId, draft);
  };
  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{ width: "100%" }}>
      <Flx ac g={0.75}>
        <TextField
          type="number"
          size="small"
          placeholder="—"
          value={draft}
          onFocus={() => {
            focusedRef.current = true;
          }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          inputProps={{ min: 0, step: 1 }}
          sx={{
            width: 64,
            "& .MuiInputBase-input": {
              fontSize: 13,
              py: 0.85,
              textAlign: "center",
            },
          }}
        />
        <Txt sx={{ fontSize: 12, color: grey[500] }}>days</Txt>
      </Flx>
    </Box>
  );
};

// Expiration cell for the upload queue. Explicit-expiration (license/passport)
// AND dual-date (statements) types get an inline date input — the SAME control
// as the Effective Start Date column — where the user picks the expiration date
// directly (the row handler derives the stored fields by type). Every other
// type shows the computed read-only expiration display.
const UploadQueueExpirationCell = ({ data, onChange }) => {
  if (
    isExplicitExpirationDocType(data?.docType) ||
    isDualDateDocType(data?.docType)
  ) {
    const expiration =
      computeExpirationDate(
        data?.reportEffectiveDate,
        data?.reportGuidelineExpirationDays,
      ) || "";
    return (
      <QueueDateInput
        value={expiration}
        onCommit={(v) => onChange?.(data?.id, v)}
        sx={{ "& .MuiInputBase-input": { fontSize: 13, py: 0.85 } }}
      />
    );
  }
  return <DocExpirationCell doc={data} />;
};

const UploadedFilesTable = ({ files, setFiles, setSelectedFiles }) => {
  const ref = useRef();
  const modalContentRef = useRef(null);

  // Assign a doc type per row (the dropdown also resolves the docGroup). When
  // the chosen type carries an auto expiration window, stamp
  // `reportGuidelineExpirationDays` so the expiration column can compute the
  // end date; clear it when the new type has none so a stale auto value from
  // a previously-picked type can't linger.
  const handleRowDocTypeChange = useCallback(
    (rowId, { docGroup, docType }) => {
      setFiles((prev) =>
        prev.map((item) => {
          if (item.id !== rowId) return item;
          // Dual-date types (statements) derive duration from the two picked
          // dates, so don't stamp a default window — start it empty.
          const days = isDualDateDocType(docType)
            ? null
            : getExpirationDaysForDocType(docType);
          return {
            ...item,
            docGroup: docGroup || null,
            docType: docType || null,
            reportGuidelineExpirationDays: days ?? null,
          };
        }),
      );
    },
    [setFiles],
  );

  const handleRowEffectiveDateChange = useCallback(
    (rowId, ymd) => {
      setFiles((prev) =>
        prev.map((item) => {
          if (item.id !== rowId) return item;
          // Dual-date types: changing the start date preserves the currently
          // picked expiration and re-derives the duration from it.
          if (isDualDateDocType(item.docType)) {
            const currentExp = computeExpirationDate(
              item.reportEffectiveDate,
              item.reportGuidelineExpirationDays,
            );
            return { ...item, ...deriveDualDateFields(ymd, currentExp) };
          }
          return { ...item, reportEffectiveDate: ymd || null };
        }),
      );
    },
    [setFiles],
  );

  // Manual override of the validity duration — only reachable for soft /
  // questionable (and unmapped) doc types, whose duration cell renders an
  // editable input. Empty clears the value (so the expiration goes blank).
  const handleRowDurationChange = useCallback(
    (rowId, raw) => {
      const parsed = raw === "" || raw == null ? null : Number(raw);
      const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
      setFiles((prev) =>
        prev.map((item) =>
          item.id === rowId
            ? { ...item, reportGuidelineExpirationDays: next }
            : item,
        ),
      );
    },
    [setFiles],
  );

  // The user picks the expiration date directly. Explicit-expiration types
  // (license/passport) back into effective=today + duration=(expiration −
  // today); dual-date types (statements) preserve the picked start and set
  // duration=(expiration − start).
  const handleRowExpirationChange = useCallback(
    (rowId, expirationYmd) => {
      setFiles((prev) =>
        prev.map((item) => {
          if (item.id !== rowId) return item;
          const derived = isDualDateDocType(item.docType)
            ? deriveDualDateFields(item.reportEffectiveDate, expirationYmd)
            : deriveFromExpirationDate(expirationYmd);
          return { ...item, ...derived };
        }),
      );
    },
    [setFiles],
  );

  const groupRows = useMemo(() => {
    const uniqueGroups = new Set(files.map((f) => f.docGroup));
    return uniqueGroups.size > 1;
  }, [files]);

  // Order the queue by docGroup A→Z so the grouped sections read
  // alphabetically. Stable sort keeps each file's order within its group.
  const sortedFiles = useMemo(
    () =>
      [...(files || [])].sort((a, b) =>
        (a?.docGroup || "Uncategorized").localeCompare(
          b?.docGroup || "Uncategorized",
        ),
      ),
    [files],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Filename",
        field: "file_display_name",
        flex: 1,
        minWidth: 150,
        tooltipValueGetter: (p) => {
          return `Rename ${p?.data?.file_display_name}`;
        },
        cellEditor: "agTextCellEditor",
        editable: true,
        cellRenderer: (params) => {
          // Doc type moved to its own dropdown column, so the filename cell
          // is just the (editable) name now.
          return (
            <Flx fw jb ac>
              <Txt fontSize={12}>{params.value}</Txt>
            </Flx>
          );
        },
        cellStyle: {
          display: "flex",
          alignItems: "center",
          cursor: "text",
          gap: "6px",
          // paddingLeft: "0px",
        },
      },
      {
        field: "docGroup",
        rowGroup: groupRows,
        hide: true,
        valueGetter: (params) => {
          return params?.data?.docGroup || "Uncategorized";
        },
      },
      {
        colId: "docTypeSelect",
        headerName: "Document Type",
        field: "docType",
        width: 260,
        // width: 320,
        flex: 0,
        editable: false,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          overflow: "visible",
        },
        cellRenderer: (params) =>
          params.node.group ? null : (
            <UploadQueueDocTypeCell
              data={params.data}
              onChange={handleRowDocTypeChange}
            />
          ),
      },
      {
        colId: "reportEffectiveDate",
        headerName: "Valid From",
        field: "reportEffectiveDate",
        width: 200,
        flex: 0,
        editable: false,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (params) =>
          params.node.group ? null : (
            <UploadQueueEffectiveDateCell
              data={params.data}
              onChange={handleRowEffectiveDateChange}
            />
          ),
      },
      {
        colId: "reportGuidelineExpirationDays",
        headerName: "Valid For",
        field: "reportGuidelineExpirationDays",
        width: 110,
        maxWidth: 110,
        flex: 0,
        editable: false,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (params) =>
          params.node.group ? null : (
            <UploadQueueDurationCell
              data={params.data}
              onChange={handleRowDurationChange}
            />
          ),
      },
      {
        colId: "expiration",
        headerName: "Expires",
        width: 200,
        flex: 0,
        editable: false,
        sortable: false,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (params) =>
          params.node.group ? null : (
            <UploadQueueExpirationCell
              data={params.data}
              onChange={handleRowExpirationChange}
            />
          ),
      },

      {
        width: 38,
        headerName: "",
        pinned: "right",
        cellStyle: {
          paddingRight: 0,
          paddingLeft: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        },
        field: "delete",
        cellRenderer: () => {
          return <CloseRounded className="thin" color="error" />;
        },
      },
    ],
    [
      groupRows,
      handleRowDocTypeChange,
      handleRowEffectiveDateChange,
      handleRowDurationChange,
      handleRowExpirationChange,
    ],
  );

  const handleCellValueChanged = useCallback(
    (params) => {
      const { colDef, data, newValue } = params;
      const updatedData = { ...data };

      if (colDef.field === "file_display_name") {
        const originalExtension = data.name.split(".").pop().toLowerCase();
        const newValueWithoutExt = newValue.replace(/\.[^/.]+$/, "");
        updatedData.file_display_name = `${newValueWithoutExt}.${originalExtension}`;
      }

      if (colDef.field === "docGroup") {
        updatedData.docGroup = newValue;
        updatedData.docType = "";
      }

      if (colDef.field === "docType") {
        updatedData.docType = newValue;
      }

      setFiles((prev) =>
        prev.map((item) => (item.id === data.id ? updatedData : item)),
      );

      setTimeout(() => {
        if (ref.current?.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [setFiles],
  );

  // Selection checkboxes are always on — checking one or more files reveals
  // the document-type picker below the queue. (Previously gated behind an
  // "edit" toggle, which has been removed.)
  const rowSelection = useMemo(() => ({ mode: "multiRow" }), []);

  const onCellClicked = useCallback(
    (params) => {
      const { colDef, data } = params;

      if (colDef.field === "delete") {
        setFiles((prev) => prev.filter((item) => item.id !== data.id));
        return;
      }

      if (colDef.field === "parse" && !params.node.group) {
        setFiles((prev) =>
          prev.map((item) =>
            item.id === data.id ? { ...item, parse: !item.parse } : item,
          ),
        );

        setTimeout(() => {
          if (ref.current?.api) {
            ref.current.api.refreshCells({
              rowNodes: [params.node],
              columns: ["parse"],
            });
          }
        }, 0);
      }
    },
    [setFiles],
  );

  const handleRowSelectionChange = useCallback(
    (params) => {
      const selected = params.api.getSelectedRows();
      setSelectedFiles(selected);
    },
    [setSelectedFiles],
  );

  const onRowClicked = useCallback((params) => {
    if (params.node.group) {
      params.node.setExpanded(!params.node.expanded);
    }
  }, []);

  const getRowStyle = useCallback((params) => {
    if (params.node.group) {
      return {
        fontWeight: 700,
        // fontStyle: "italic",
        fontSize: "13px",
        background: "#1b3a4b13",
        // background: "#f8fafc",
        // fontWeight: 600,
        // borderLeft: "3px solid #e2e8f0",
        borderColor: "#e2e8f0",
        cursor: "pointer",
      };
    }

    return undefined;
  }, []);

  if (!files?.length) {
    return null;
  }

  return (
    <Box
      sx={{
        borderRadius: "0 0 8px 8px",
        ".ag-header": {
          background: "#ffffff !important",
          // background: "#1b3a4b1f !important",
          borderBottom: "1px solid #e2e8f0",
          ".ag-header-cell": {
            // color: "#1B3A4B !important",
            color: "#64748b !important",
            textTransform: "uppercase !important",
            fontSize: "11px !important",
            fontWeight: `700 !important`,
            // letterSpacing: "0.7px !important",
          },
        },

        ".ag-column-first": {
          display: "flex",
          alignItems: "center",
        },
        ".ag-row": {
          borderColor: "#e2e8f0",
        },
        ".ag-row-last": {
          borderBottom: "none !important",
        },
      }}
    >
      <AgGridTableWrapper
        compactTable
        // simpleTable
        //
        noBorder
        noBorderRadius
        suppressMinHeightWhenPopulated={files}
      >
        <div ref={modalContentRef}>
          <AgGridReact
            ref={ref}
            rowData={sortedFiles}
            columnDefs={columnDefs}
            singleClickEdit
            defaultColDef={{
              // Drop the per-column header 3-dot menu button and the filter
              // funnel — this is a small fixed review queue, not a sortable/
              // filterable data table.
              suppressHeaderMenuButton: true,
              suppressHeaderFilterButton: true,
              sortable: false,
              // Vertically center every cell's content regardless of row height.
              cellStyle: { display: "flex", alignItems: "center" },
              // When a keystroke originates from a text input / the doc-type
              // autocomplete inside a cell, let the browser/MUI handle it
              // instead of AG Grid's cell navigation. Without this, arrow keys
              // can't navigate the doc-type dropdown and Ctrl+A selects all
              // grid rows instead of the text in the duration/filename input.
              suppressKeyboardEvent: (params) => {
                const target = params?.event?.target;
                if (!target) return false;
                const tag = target.tagName;
                return (
                  tag === "INPUT" ||
                  tag === "TEXTAREA" ||
                  target.isContentEditable === true
                );
              },
            }}
            rowHeight={58}
            headerHeight={34}
            onCellClicked={onCellClicked}
            domLayout="autoHeight"
            tooltipShowDelay={100}
            tooltipMouseTrack
            // headerHeight={38}
            // Stable row identity so editing a row (e.g. typing a duration)
            // reuses the existing row/cell instances instead of recreating
            // them — keeps input focus while typing.
            getRowId={(p) => p.data.id}
            getRowStyle={getRowStyle}
            onRowClicked={onRowClicked}
            rowSelection={rowSelection}
            onSelectionChanged={handleRowSelectionChange}
            groupDisplayType="groupRows"
            // Order the grouped category sections A→Z by group key. AG Grid
            // doesn't derive group order from rowData order, so sorting the
            // rows alone isn't enough — this comparator sorts the groups
            // themselves on every grouping pass.
            initialGroupOrderComparator={(params) =>
              (params?.nodeA?.key || "").localeCompare(params?.nodeB?.key || "")
            }
            groupDefaultExpanded={groupRows ? 1 : -1}
            onCellValueChanged={handleCellValueChanged}
            stopEditingWhenCellsLoseFocus
            popupParent={modalContentRef.current}
          />
        </div>
      </AgGridTableWrapper>
    </Box>
  );
};

// In UploadLoanDocumentByGroupProcess — add onRemoveFile handler and pass it down

// UploadLoanDocumentByGroupProcess — masonry layout via CSS columns
// Drop this in to replace the existing component (and DocGroupUpload below it).
// FlxGrid is no longer used here; the column count is responsive via CSS.

// =====================================================================
// Upload By Category — sidebar + panel layout.
//
// Left rail lists every doc category (filterable). Selecting one shows that
// category's document types as drop cards in the panel on the right. Every
// category renders at once; the rail scrolls to them.
// =====================================================================

// ── Category rail = anchor nav ───────────────────────────────────────
// The rail works like the Admin → Pricing "On this page" rail (see
// AdminPricingScreen/pricingUi.js SectionRail): the panel always renders every
// category, clicking a rail item scrolls to that category's section, and the
// highlight follows the scroll position. It used to filter the panel down to a
// single category, which hid the rest of the form behind a click.
const docGroupAnchorId = (gr) =>
  `doc-group-${String(gr)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;

/** Nearest ancestor that actually scrolls, or null for the document. */
const nearestScrollParent = (el) => {
  let node = el?.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
};

// How far below the top of the scroll container a section should land.
//
// `--drilldown-nav-h` is published by LoanDrilldownScreen on the SCROLLER
// (`#overflow-root`), not on :root — reading it off documentElement always
// yields "" and lands the section under the pinned nav. Read it from the
// scroll root (or any descendant, which inherits it). In the modal the pane is
// portaled outside the drilldown, so the var is unset there and the fallback
// 0 is exactly right: that pane's own top edge is the mark.
const CATEGORY_ANCHOR_GAP = 16;
const categoryAnchorOffset = (root) => {
  const raw = getComputedStyle(
    root || document.documentElement,
  ).getPropertyValue("--drilldown-nav-h");
  const navH = parseInt(raw, 10);
  return (Number.isFinite(navH) ? navH : 0) + CATEGORY_ANCHOR_GAP;
};

// A single document-type drop target: label on the left, a queued-file count
// chip (when any) and "+" on the right. Reproduces FileUploadField's drag/drop
// + click-to-browse behavior with the row layout from the category-panel
// design.
const DocTypeDropCard = ({ label, docGroup, docType, onFilesAdded, count }) => {
  const inputId = `cat-upload-${docGroup}-${docType}`;
  const dragCounter = useRef(0);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const emitFiles = (rawFiles) => {
    if (!rawFiles?.length) return;
    const newFiles = rawFiles.map((file) => ({
      id: uuidv4(),
      file,
      name: file.name,
      file_display_name: file.name,
      docGroup,
      docType,
    }));
    onFilesAdded(newFiles);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setIsDraggingOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDraggingOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDraggingOver(false);
    if (e.dataTransfer?.files) emitFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <Box
      onClick={() => document.getElementById(inputId)?.click()}
      onDragEnter={handleDragEnter}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        // 8px — Box `gap` is theme-spaced (×8), so 1, not 8.
        gap: 1,
        // 8px 10px 8px 12px
        pt: 1,
        pr: 1.25,
        pb: 1,
        pl: 1.5,
        minHeight: 40,
        cursor: "pointer",
        userSelect: "none",
        borderRadius: "8px",
        border: "1.5px dashed",
        borderColor: isDraggingOver ? "primary.main" : "#CBD5DA",
        background: isDraggingOver ? "#eef4ff" : "#fff",
        transition: "all 120ms ease-in-out",
        "&:hover": { borderColor: "#94a3b8", background: "#f8fafc" },
      }}
    >
      <Txt
        sx={{
          fontSize: "12.5px",
          fontWeight: 500,
          color: "#48545c",
          // Wrap long doc-type names onto multiple lines instead of truncating
          // with an ellipsis (the card grows taller to fit).
          flex: 1,
          minWidth: 0,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
        }}
      >
        {label}
      </Txt>
      {/* How many files are queued against this type. Replaces the old list of
          filenames under the card — the queue below is where files get
          reviewed, renamed and removed, so repeating them here just made the
          grid jump around as files landed. */}
      {count > 0 && (
        <Flx
          center
          sx={{
            flexShrink: 0,
            minWidth: 18,
            height: 18,
            px: 0.5,
            borderRadius: "999px",
            background: teal[100],
            border: `1px solid ${teal[200]}`,
            color: teal[700],
            fontSize: "10.5px",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {count}
        </Flx>
      )}
      <Flx
        center
        sx={{
          flexShrink: 0,
          width: 18,
          height: 18,
          borderRadius: "5px",
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          color: "primary.main",
        }}
      >
        <Add sx={{ fontSize: 13 }} />
      </Flx>
      <input
        id={inputId}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files) emitFiles(Array.from(e.target.files));
        }}
      />
    </Box>
  );
};

// Responsive grid of doc-type drop cards for a single category. Each card
// carries a count chip for the files queued against that type; the files
// themselves live in the FixedUploadQueue below, which is where they're
// reviewed and removed. Exported so the Entity tab can render the entity
// doc-groups' drop cards with the same behavior (drop → shared queue →
// FixedUploadQueue).
export const CategoryDocTypeGrid = ({
  docGroup,
  addedFiles,
  onFilesAdded,
  // `onRemoveFile` is still passed by callers but no longer used here — removal
  // is the queue's job now that the per-card file list is gone.
  search,
  // Min drop-card width for the responsive grid. Callers (the modal and the
  // Entity tab) pass narrower values for their tighter columns; the Documents
  // tab takes this default.
  minCardWidth = 280,
}) => {
  const { getDocTypesByGroup } = useFileDocGroupAndTypesHook();

  // Sort the category's doc types A→Z so the upload chips read alphabetically
  // rather than in raw taxonomy order.
  const docTypes = useMemo(() => {
    const types = getDocTypesByGroup(docGroup) || [];
    return [...types].sort((a, b) => a.localeCompare(b));
  }, [getDocTypesByGroup, docGroup]);

  const filteredDocTypes = useMemo(() => {
    if (!search?.trim()) return docTypes;
    const lower = search.toLowerCase();
    // If the category name itself matched the search, keep all of its types.
    if (docGroup.toLowerCase().includes(lower)) return docTypes;
    return docTypes.filter((dt) => dt.toLowerCase().includes(lower));
  }, [docTypes, search, docGroup]);

  const filesByDocType = useMemo(() => {
    const map = {};
    addedFiles?.forEach((f) => {
      if (f?.docGroup === docGroup && f?.docType) {
        if (!map[f.docType]) map[f.docType] = [];
        map[f.docType].push(f);
      }
    });
    return map;
  }, [addedFiles, docGroup]);

  if (isEmpty(filteredDocTypes)) return null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}px, 1fr))`,
        gap: "6px",
        alignItems: "start",
      }}
    >
      {filteredDocTypes.map((dt) => (
        <DocTypeDropCard
          key={dt}
          label={dt}
          docGroup={docGroup}
          docType={dt}
          onFilesAdded={onFilesAdded}
          count={(filesByDocType[dt] || []).length}
        />
      ))}
    </Box>
  );
};

// One row in the left category rail.
const CategorySidebarItem = ({ label, active, count, onClick, sx }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      cursor: "pointer",
      userSelect: "none",
      px: 1.5,
      py: 0.85,
      borderRadius: "2px",
      // borderRadius: "6px",
      // border: "1px solid",
      borderLeft: active ? "3px solid" : "none",
      borderColor: active ? "primary.main" : "#e2e8f0",
      // boxShadow: active ? "none" : "0 1px 2px #0f172a0d",
      background: active ? "#eef2ff" : "#ffffff",
      transition: "all 120ms ease-in-out",
      "&:hover": {
        borderColor: active ? "primary.main" : "#cbd5e1",
        background: active ? "#eef2ff" : "#f8fafc",
      },
      ...sx,
    }}
  >
    <Txt
      sx={{
        fontSize: "11.5px",
        fontWeight: active ? 600 : 500,
        color: active ? "primary.main" : "#334155",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Txt>
    {count > 0 && (
      <Box
        sx={{
          flexShrink: 0,
          fontSize: "10px",
          fontWeight: 700,
          color: "#fff",
          background: (t) => t.palette.primary.main,
          borderRadius: "10px",
          minWidth: 18,
          height: 18,
          px: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

const UploadLoanDocumentByGroupProcess = ({
  addedFiles,
  onFilesAdded,
  onRemoveFile,
  queueContent,
  // In the modal the two columns become full-height panes that each own their
  // scroll (a left rail flush to the modal edge, no rounded corners). In the tab
  // (default) the left rail is a rounded, sticky card and the page scrolls.
  paneLayout = false,
  minCardWidth,
}) => {
  const { fileDocGroups, getDocTypesByGroup } = useFileDocGroupAndTypesHook();
  const [search, setSearch] = useState("");
  // Which rail item is highlighted. Driven by scroll position, not by a
  // selection — the panel always shows every category.
  const [activeGroup, setActiveGroup] = useState(null);
  // Suppresses spy updates while a click-triggered smooth scroll is in flight,
  // otherwise the sections it passes on the way steal the highlight.
  const spyLockRef = useRef(0);
  // The section to play the "you landed here" pulse on. The nonce re-keys the
  // header so clicking the same rail item twice replays the animation instead
  // of doing nothing.
  const [flash, setFlash] = useState({ group: null, nonce: 0 });
  const flashTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(flashTimerRef.current), []);

  // Sidebar list: filter by category name OR any doc type within it.
  const filteredGroups = useMemo(() => {
    const lower = search?.trim()?.toLowerCase();
    const matched = lower
      ? fileDocGroups.filter(
          (gr) =>
            gr.toLowerCase().includes(lower) ||
            getDocTypesByGroup(gr)?.some((dt) =>
              dt.toLowerCase().includes(lower),
            ),
        )
      : fileDocGroups;
    // Sort the categories A→Z so the nav + panels read alphabetically.
    return [...matched].sort((a, b) => a.localeCompare(b));
  }, [fileDocGroups, search, getDocTypesByGroup]);

  // Scroll-spy: whichever section's top has most recently passed under the
  // container's top edge owns the highlight.
  useEffect(() => {
    if (isEmpty(filteredGroups)) return;
    const first = document.getElementById(docGroupAnchorId(filteredGroups[0]));
    if (!first) return;
    const root = nearestScrollParent(first);
    const target = root || window;
    const offset = categoryAnchorOffset(root);

    const onScroll = () => {
      if (Date.now() < spyLockRef.current) return;
      const rootTop = root ? root.getBoundingClientRect().top : 0;
      let current = filteredGroups[0];
      filteredGroups.forEach((gr) => {
        const el = document.getElementById(docGroupAnchorId(gr));
        if (!el) return;
        if (el.getBoundingClientRect().top - rootTop <= offset + 24) {
          current = gr;
        }
      });
      setActiveGroup(current);
    };

    onScroll();
    target.addEventListener("scroll", onScroll, { passive: true });
    return () => target.removeEventListener("scroll", onScroll);
  }, [filteredGroups, paneLayout]);

  const countForGroup = useCallback(
    (gr) => size(addedFiles?.filter((f) => f?.docGroup === gr)),
    [addedFiles],
  );

  // Collapsible group sections in the panel. Track the *collapsed* set so the
  // default (empty set) leaves every group expanded.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleCollapsed = useCallback((gr) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(gr) ? next.delete(gr) : next.add(gr);
      return next;
    });
  }, []);

  // Scroll to a category's section, open it if it was collapsed (you can't
  // meaningfully land on a collapsed one), and pulse its header so it's obvious
  // where you arrived.
  const goToGroup = useCallback((gr) => {
    spyLockRef.current = Date.now() + 700;
    setActiveGroup(gr);
    setCollapsed((prev) => {
      if (!prev.has(gr)) return prev;
      const next = new Set(prev);
      next.delete(gr);
      return next;
    });
    setFlash((prev) => ({ group: gr, nonce: prev.nonce + 1 }));
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(
      () => setFlash({ group: null, nonce: 0 }),
      1600,
    );
    const el = document.getElementById(docGroupAnchorId(gr));
    if (!el) return;
    const root = nearestScrollParent(el);
    const offset = categoryAnchorOffset(root);
    if (root) {
      root.scrollTo({
        top:
          root.scrollTop +
          el.getBoundingClientRect().top -
          root.getBoundingClientRect().top -
          offset,
        behavior: "smooth",
      });
    } else {
      window.scrollTo({
        top: window.scrollY + el.getBoundingClientRect().top - offset,
        behavior: "smooth",
      });
    }
  }, []);

  // The panel always renders every (filtered) category — the rail navigates
  // rather than filters.
  const groupsToRender = filteredGroups;

  return (
    <Flx
      sx={{
        gap: paneLayout ? 0 : 2,
        alignItems: paneLayout ? "stretch" : "flex-start",
        // Fill the parent column via flex-grow (not height:100%, which resolves
        // indefinite → 0 inside a flex-column ancestor and collapses the panes).
        ...(paneLayout && { flex: 1, minHeight: 0 }),
      }}
    >
      {/* ---- Left rail: search + category list ---- */}
      <Flx
        column
        // g={1}
        sx={{
          width: 248,
          flexShrink: 0,
          background: "#ffffff",
          ...(paneLayout
            ? {
                // Full-height left pane flush to the modal edge — no rounding,
                // just a right divider; its category list owns the scroll.
                position: "static",
                borderRight: "1px solid #e7ebf1",
                borderRadius: 0,
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
              }
            : {
                position: "sticky",
                // Offset by the drilldown's pinned tab nav.
                top: "calc(var(--drilldown-nav-h, 0px) + 8px)",
                alignSelf: "flex-start",
                border: "1px solid #e7ebf1",
                borderRadius: "8px",
                zIndex: 11,
              }),
        }}
      >
        <Box sx={{ p: 1.5, flexShrink: 0 }}>
          <SearchInput
            value={search}
            placeholder="Search document type..."
            onChange={setSearch}
          />
        </Box>
        <Flx
          column
          g={0.75}
          sx={{
            borderTop: "1px solid #e7ebf1",
            p: 1.25,
            ...(paneLayout && {
              flexGrow: 1,
              minHeight: 0,
              overflowY: "auto",
            }),
          }}
        >
          {/* The rail navigates rather than filters now, so the old "All
              Document Groups & Types" row had nothing left to switch to — a
              caption saying what the list below IS does the job instead. */}
          <Flx
            ac
            jb
            g={1}
            sx={{
              px: 1.25,
              pb: 1,
              borderBottom: "1px solid #e7ebf1",
              mb: 0.25,
            }}
          >
            <Txt
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                color: "#94a3b8",
              }}
            >
              Document Groups
            </Txt>
            {size(addedFiles) > 0 && (
              <Txt sx={{ fontSize: 10.5, fontWeight: 700, color: teal[700] }}>
                {size(addedFiles)} queued
              </Txt>
            )}
          </Flx>
          {isEmpty(filteredGroups) ? (
            <Txt sx={{ fontStyle: "italic", p: 1.5, fontSize: 12 }}>
              No categories found.
            </Txt>
          ) : (
            filteredGroups.map((gr) => (
              <CategorySidebarItem
                key={gr}
                label={gr}
                active={activeGroup === gr}
                count={countForGroup(gr)}
                onClick={() => goToGroup(gr)}
              />
            ))
          )}
        </Flx>
      </Flx>

      {/* ---- Right column: doc-type groups, with the queue beneath them ---- */}
      <Flx
        column
        sx={{
          flexGrow: 1,
          minWidth: 0,
          ...(paneLayout
            ? {
                // Right pane owns its own scroll, with a little breathing room
                // top and bottom.
                height: "100%",
                minHeight: 0,
                overflowY: "auto",
                px: 3,
                py: 2,
              }
            : { minHeight: 280 }),
        }}
      >
        {isEmpty(groupsToRender) ? (
          <Txt sx={{ fontStyle: "italic", p: 1, fontSize: 12 }}>
            No document types found.
          </Txt>
        ) : (
          groupsToRender.map((gr, idx) => {
            const open = !collapsed.has(gr);
            return (
              <Box
                key={gr}
                // Scroll target for the left rail — see docGroupAnchorId.
                id={docGroupAnchorId(gr)}
                sx={{
                  // Backstop for any native anchor jump (the rail does its own
                  // math): clear the drilldown's pinned nav, which publishes
                  // its height on the scroller. Unset in the modal → 0.
                  scrollMarginTop: `calc(var(--drilldown-nav-h, 0px) + ${CATEGORY_ANCHOR_GAP}px)`,
                  border: "1px solid #e7ebf1",
                  borderRadius: "8px",
                  background: "#ffffff",
                  overflow: "hidden",
                  // Keep each group card at its natural height. As a flex item in
                  // the scrollable column, the default flex-shrink:1 would squash
                  // the cards (clipping their fields) instead of letting the pane
                  // scroll.
                  flexShrink: 0,
                  mb: idx + 1 < groupsToRender.length ? 1.5 : 0,
                }}
              >
                <Flx
                  ac
                  jb
                  g={1}
                  // Re-keying on the flash nonce remounts the header, which is
                  // what restarts the CSS animation when the same rail item is
                  // clicked twice in a row.
                  key={flash.group === gr ? `${gr}-flash-${flash.nonce}` : gr}
                  onClick={() => toggleCollapsed(gr)}
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    px: 1.25,
                    py: 1,
                    "&:hover": { background: "#f8fafc" },
                    // "You landed here" pulse — a teal wash with a left rule
                    // that fades out. Without it a jump to a section that
                    // already fills the viewport looks like nothing happened.
                    ...(flash.group === gr && {
                      animation: "docGroupArrive 1.5s ease-out 1",
                      "@keyframes docGroupArrive": {
                        "0%": {
                          background: teal[100],
                          boxShadow: `inset 3px 0 0 ${teal[500]}`,
                        },
                        "60%": {
                          background: teal[50],
                          boxShadow: `inset 3px 0 0 ${teal[500]}`,
                        },
                        "100%": {
                          background: "transparent",
                          boxShadow: "inset 3px 0 0 transparent",
                        },
                      },
                    }),
                  }}
                >
                  <Flx column sx={{ minWidth: 0 }}>
                    <Txt
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {gr}
                    </Txt>
                    <Txt sx={{ fontSize: 10, color: "#94a3b8" }}>
                      Drop files onto a document type, or click to browse
                    </Txt>
                  </Flx>
                  {open ? (
                    <KeyboardArrowUp fontSize="small" />
                  ) : (
                    <KeyboardArrowDown fontSize="small" />
                  )}
                </Flx>
                {/* In paneLayout (modal) render the open content directly:
                    MUI Collapse measures its content height on mount, which
                    reads 0 for the grid inside the modal's overflow:auto pane
                    before layout settles — leaving every group visually
                    collapsed. The tab keeps the animated Collapse. */}
                {paneLayout ? (
                  open && (
                    <Box sx={{ px: 1.25, pb: 1.25, pt: 0.25 }}>
                      <CategoryDocTypeGrid
                        docGroup={gr}
                        addedFiles={addedFiles}
                        onFilesAdded={onFilesAdded}
                        onRemoveFile={onRemoveFile}
                        minCardWidth={minCardWidth}
                        search={search}
                      />
                    </Box>
                  )
                ) : (
                  <Collapse in={open} unmountOnExit>
                    <Box sx={{ px: 1.25, pb: 1.25, pt: 0.25 }}>
                      <CategoryDocTypeGrid
                        docGroup={gr}
                        addedFiles={addedFiles}
                        onFilesAdded={onFilesAdded}
                        onRemoveFile={onRemoveFile}
                        minCardWidth={minCardWidth}
                        search={search}
                      />
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })
        )}
        {queueContent}
      </Flx>
    </Flx>
  );
};

export default LoanDrilldownDocumentsTab;
